// DESIGN_AUDIT Phase 11C: hardcoded colors replaced with CSS vars
// DESIGN_AUDIT Phase 11A fixes applied:
//   FIX 3: handleAnalyzeBids changed from fetch('/api/projects/...') to fetch(`${VITE_API_URL}/projects/...`)
//          Removes Vercel proxy routing for long-running AI call (Rule 8 compliance)
// DESIGN_AUDIT Phase 11B: all modals converted to ReactDOM.createPortal
import ReactDOM from 'react-dom';
import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Pencil, Trash2, X, FolderKanban, DollarSign,
  Calendar, Search, ChevronDown, AlertCircle, ChevronRight,
  Upload, FileText, ArrowLeft, Star, Sparkles, Check, Phone, Mail, Brain,
  Lock, ChevronLeft, Send, ChevronUp,
} from 'lucide-react';
import { api } from '../api/client';
import { format, parseISO } from 'date-fns';
import apiClient from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { loadPdfJs } from '../components/PdfViewer.tsx';
import PageFocusToggle from '../components/PageFocusToggle.tsx';
import DatePicker from '../components/DatePicker.tsx';
import Toast, { useToast } from '../components/Toast';

// ─── Inline PDF viewer for bid cards ─────────────────────────────────────────
function BidPdfViewer({ url, onClose }: { url: string; onClose: () => void }) {
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(1.8);
  const [currentPage, setCurrentPage] = useState(1);
  const pageRefs = useRef<(HTMLCanvasElement | null)[]>([]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => { window.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [onClose]);

  useEffect(() => {
    setLoading(true);
    loadPdfJs()
      .then(() => (window as any).pdfjsLib.getDocument({ url, withCredentials: false }).promise)
      .then((doc: any) => { setPdfDoc(doc); setLoading(false); })
      .catch(() => setLoading(false));
  }, [url]);

  useEffect(() => {
    if (!pdfDoc) return;
    const numPages = pdfDoc.numPages;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < numPages; i++) {
        if (cancelled) return;
        const canvas = pageRefs.current[i];
        if (!canvas) continue;
        try {
          const page = await pdfDoc.getPage(i + 1);
          if (cancelled) return;
          const vp = page.getViewport({ scale });
          canvas.width = vp.width; canvas.height = vp.height;
          const ctx = canvas.getContext('2d');
          if (ctx) page.render({ canvasContext: ctx, viewport: vp });
        } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [pdfDoc, scale]);

  useEffect(() => {
    if (!pdfDoc) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) { const idx = pageRefs.current.indexOf(e.target as HTMLCanvasElement); if (idx !== -1) setCurrentPage(idx + 1); } }); },
      { threshold: 0.5 }
    );
    pageRefs.current.forEach((c) => { if (c) observer.observe(c); });
    return () => observer.disconnect();
  }, [pdfDoc]);

  const numPages = pdfDoc?.numPages ?? 0;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.95)', overflow: 'auto' }} onClick={onClose}>
      <div style={{ position: 'sticky', top: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', padding: '8px 16px', display: 'flex', gap: 12, zIndex: 10, alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)' }} onClick={(e) => e.stopPropagation()}>
        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', fontWeight: 500, minWidth: 90 }}>Page {currentPage} of {numPages || '…'}</span>
        <button onClick={() => setScale((s) => Math.min(s + 0.2, 3.0))} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#fff', fontSize: '0.875rem', fontWeight: 600 }}>+</button>
        <button onClick={() => setScale((s) => Math.max(s - 0.2, 0.8))} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#fff', fontSize: '0.875rem', fontWeight: 600 }}>−</button>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8125rem' }}>{Math.round(scale * 100)}%</span>
        <button onClick={onClose} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center' }}><X size={18} /></button>
      </div>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <div style={{ width: 22, height: 22, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.08)', borderTopColor: '#3b82f6', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
          {numPages > 0 && Array.from({ length: numPages }, (_, i) => (
            <canvas key={i} ref={(el) => { pageRefs.current[i] = el; }} style={{ display: 'block', boxShadow: '0 4px 24px rgba(0,0,0,0.6)', borderRadius: 4, maxWidth: '100%' }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Email memory ─────────────────────────────────────────────────────────────
const APPROVAL_EMAILS_KEY = 'proqure_approval_emails';
function getSavedEmails(): string[] {
  try { return JSON.parse(localStorage.getItem(APPROVAL_EMAILS_KEY) || '[]'); } catch { return []; }
}
function saveApprovalEmail(email: string) {
  const saved = getSavedEmails();
  if (!saved.includes(email)) {
    saved.unshift(email);
    localStorage.setItem(APPROVAL_EMAILS_KEY, JSON.stringify(saved.slice(0, 20)));
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';

interface LineItem {
  id?: number;
  description: string;
  quantity: number;
  unit_cost: number;
  total_cost?: number;
}

interface QuoteLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface Quote {
  id: number;
  project_id: number;
  vendor_name: string | null;
  vendor_contact: string | null;
  vendor_email: string | null;
  vendor_phone: string | null;
  quote_amount: string | null;
  file_url: string | null;
  file_type: string | null;
  file_name: string | null;
  notes: string | null;
  is_recommended: boolean;
  line_items: QuoteLineItem[];
  created_at: string;
}

interface Subcontract {
  id: number;
  quote_id: number;
  project_id: number;
  vendor_name: string | null;
  description: string | null;
  amount: string | null;
  line_items: QuoteLineItem[];
  notes: string | null;
  created_at: string;
}

type SubcontractForm = {
  vendor_name: string;
  description: string;
  amount: string;
  notes: string;
  line_items: QuoteLineItem[];
};

const EMPTY_SUB_FORM: SubcontractForm = {
  vendor_name: '', description: '', amount: '', notes: '', line_items: [],
};

interface BidAnalysis {
  best_price: { vendor: string; amount: number; reason: string };
  best_value: { vendor: string; reason: string };
  risks: { vendor: string; risk: string }[];
  missing_info: { vendor: string; missing: string }[];
  recommendation: { vendor: string; reasoning: string };
}

interface Project {
  id: number;
  name: string;
  description: string | null;
  status: ProjectStatus;
  workflow_status: string;
  ai_analysis: BidAnalysis | null;
  vendor_id: number | null;
  vendor_name: string | null;
  estimated_cost: string | null;
  actual_cost: string | null;
  start_date: string | null;
  end_date: string | null;
  notes: string | null;
  scheduled_date: string | null;
  scheduled_date_closed: boolean;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  line_items: LineItem[];
}

// ─── Workflow stages ──────────────────────────────────────────────────────────
const WORKFLOW_STAGES: { key: string; label: string }[] = [
  { key: 'obtaining_bids', label: 'Obtaining Bids' },
  { key: 'approval_sent', label: 'Approval Sent' },
  { key: 'approved', label: 'Approved' },
  { key: 'scheduling', label: 'Scheduling' },
  { key: 'completed', label: 'Completed' },
  { key: 'paid', label: 'Paid' },
];

function getWorkflowIndex(status: string): number {
  const idx = WORKFLOW_STAGES.findIndex((s) => s.key === status);
  return idx >= 0 ? idx : 0;
}

function getWorkflowLabel(status: string): string {
  return WORKFLOW_STAGES.find((s) => s.key === status)?.label ?? status;
}

interface Vendor { id: number; name: string; }

type ProjectForm = {
  name: string; description: string; status: ProjectStatus;
  vendor_id: string; estimated_cost: string; actual_cost: string;
  start_date: string; end_date: string; notes: string;
  line_items: LineItem[];
};

type QuoteForm = {
  vendor_name: string;
  vendor_contact: string;
  vendor_email: string;
  vendor_phone: string;
  quote_amount: string;
  notes: string;
  line_items: QuoteLineItem[];
};

const EMPTY_FORM: ProjectForm = {
  name: '', description: '', status: 'planning', vendor_id: '',
  estimated_cost: '', actual_cost: '', start_date: '', end_date: '', notes: '',
  line_items: [],
};

const EMPTY_QUOTE_FORM: QuoteForm = {
  vendor_name: '', vendor_contact: '', vendor_email: '', vendor_phone: '',
  quote_amount: '', notes: '', line_items: [],
};

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<ProjectStatus, { label: string; color: string; bg: string }> = {
  planning:  { label: 'Planning',   color: '#818cf8', bg: 'rgba(99,102,241,0.15)'  },
  active:    { label: 'Active',     color: '#34d399', bg: 'rgba(16,185,129,0.15)'  },
  on_hold:   { label: 'On Hold',    color: '#fbbf24', bg: 'rgba(245,158,11,0.15)'  },
  completed: { label: 'Completed',  color: '#60a5fa', bg: 'rgba(59,130,246,0.15)'  },
  cancelled: { label: 'Cancelled',  color: '#94a3b8', bg: 'rgba(107,114,128,0.15)' },
};

const ALL_STATUSES: ProjectStatus[] = ['planning', 'active', 'on_hold', 'completed', 'cancelled'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCurrency(val: string | number | null): string {
  if (val === null || val === undefined || val === '') return '—';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

// For genuine timestamps (e.g. created_at) -- parseISO + local format() correctly localizes a
// real instant, which is what created_at needs (an event created at 11pm Eastern on the 29th
// should still SHOW as the 29th, not get pulled to the 30th by reading UTC components).
function fmtDate(val: string | null): string {
  if (!val) return '—';
  try { return format(parseISO(val), 'MMM d, yyyy'); } catch { return val; }
}

// For plain DATE columns (start_date/end_date -- projects.start_date/end_date, DATE not
// TIMESTAMPTZ) -- deliberately NOT fmtDate/parseISO above. Same bug class as the Orders "Expected
// Delivery Date" off-by-one: parseISO on a full UTC timestamp (what server/src/db.ts used to
// return for every DATE column, and what any TIMESTAMPTZ literally at UTC midnight still looks
// like) + format()'s local rendering rolls back a day for anyone west of UTC. Reproduced directly:
// parseISO('2026-09-30T00:00:00.000Z') then format(..., 'MMM d, yyyy') in America/New_York prints
// "Sep 29, 2026". The server-side DATE type-parser fix (server/src/db.ts) already prevents this
// specific input shape from occurring today, but fmtDate here would silently reintroduce the exact
// same bug the moment start_date/end_date ever again arrive as a full timestamp -- so start_date/
// end_date get their own formatter using the same safe y/m/d local-constructor pattern already
// established elsewhere (Reports.tsx's fmtDate, Inventory.tsx's fmtSupplyDate/fmtExtDate,
// Tasks.tsx's woFmtDate, PaintUsedInPanel's fmtDate) rather than depending on the wire format
// staying exactly right forever.
function fmtProjectDate(val: string | null): string {
  if (!val) return '—';
  const s = String(val).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return val;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function projectToForm(p: Project): ProjectForm {
  return {
    name: p.name, description: p.description ?? '', status: p.status,
    vendor_id: p.vendor_id ? String(p.vendor_id) : '',
    estimated_cost: p.estimated_cost ?? '', actual_cost: p.actual_cost ?? '',
    start_date: p.start_date ? p.start_date.slice(0, 10) : '',
    end_date: p.end_date ? p.end_date.slice(0, 10) : '',
    notes: p.notes ?? '',
    line_items: p.line_items.map((li) => ({
      id: li.id, description: li.description, quantity: li.quantity, unit_cost: li.unit_cost,
    })),
  };
}

function lineItemTotal(items: LineItem[]): number {
  return items.reduce((s, li) => s + (li.quantity || 0) * (li.unit_cost || 0), 0);
}

// ─── Shared inline styles ─────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 5,
  fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', boxSizing: 'border-box',
  border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.875rem',
  outline: 'none',
};

// ─── Styled single-select dropdown (matches Vendors dark theme) ───────────────

interface SelectOption { value: string; label: string; color?: string; }

function StyledSelect({ options, value, onChange, placeholder = 'Select…', minWidth = 160 }: {
  options: SelectOption[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  minWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} style={{ position: 'relative', minWidth }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '9px 32px 9px 12px', position: 'relative',
          border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
          background: 'var(--bg-elevated)', color: selected ? 'var(--text-primary)' : 'var(--text-muted)',
          fontSize: '0.875rem', cursor: 'pointer', userSelect: 'none',
        }}
      >
        {selected?.color && (
          <div style={{ width: 10, height: 10, borderRadius: 2, background: selected.color, flexShrink: 0 }} />
        )}
        <span>{selected?.label ?? placeholder}</span>
        <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 400,
          border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
          boxShadow: 'var(--shadow-md)', overflow: 'hidden',
        }}>
          <div style={{ maxHeight: 220, overflowY: 'auto', background: 'var(--bg-elevated)' }}>
            {options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', cursor: 'pointer',
                  background: opt.value === value ? 'var(--bg-hover)' : 'transparent',
                  borderBottom: '1px solid var(--border-subtle)',
                  fontSize: '0.875rem', color: 'var(--text-primary)',
                }}
                onMouseEnter={(e) => { if (opt.value !== value) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { if (opt.value !== value) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
              >
                {opt.color && <div style={{ width: 10, height: 10, borderRadius: 2, background: opt.color, flexShrink: 0 }} />}
                <span style={{ flex: 1 }}>{opt.label}</span>
                {opt.value === value && <span style={{ fontSize: '0.75rem', color: 'var(--accent-primary)' }}>✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ProjectsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const [demoToast, setDemoToast] = useState<string | null>(null);
  const demoToastTimer = useRef<ReturnType<typeof setTimeout>>();
  function showDemoToast(msg = 'Demo mode — this action is view-only') {
    setDemoToast(msg);
    clearTimeout(demoToastTimer.current);
    demoToastTimer.current = setTimeout(() => setDemoToast(null), 3500);
  }
  function demoBlock(action: () => void) {
    if (isViewer) { showDemoToast(); return; }
    action();
  }
  const isEnterprise = user?.orgPlanTier === 'enterprise' || user?.role === 'admin';
  const isPro = user?.orgPlanTier === 'pro' && !isEnterprise;

  const { data: aiUsageData } = useQuery({
    queryKey: ['ai-usage'],
    queryFn: () => api.get<{ used: number; limit: number | null; month?: string }>('/projects/ai-usage'),
    enabled: isPro,
  });
  const aiUsed = aiUsageData?.data?.used ?? 0;
  const aiLimit = aiUsageData?.data?.limit ?? 9;

  // ── List state
  const [workflowTab, setWorkflowTab] = useState<'active' | 'completed'>('active');
  const [statusFilter, setStatusFilter] = useState<'all' | ProjectStatus>('all');
  const [search, setSearch] = useState('');

  // ── Selected project for detail view
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedQuotes, setSelectedQuotes] = useState<Quote[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);

  // ── Panel state (add/edit project)
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [form, setForm] = useState<ProjectForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);

  // ── Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState<Project | null>(null);

  // ── Quote form (for add bid modal)
  const [addBidOpen, setAddBidOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState<QuoteForm>(EMPTY_QUOTE_FORM);
  const [quoteFile, setQuoteFile] = useState<File | null>(null);
  const [quoteSaving, setQuoteSaving] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Edit bid modal
  const [editBidOpen, setEditBidOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [editQuoteForm, setEditQuoteForm] = useState<QuoteForm>(EMPTY_QUOTE_FORM);
  const [editQuoteSaving, setEditQuoteSaving] = useState(false);
  const [editQuoteError, setEditQuoteError] = useState('');

  // ── AI PDF extraction modal
  const AI_LOADING_MSGS = ['Uploading PDF...', 'Reading document...', 'Extracting data with AI...', 'Almost done...'];
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiPdfFile, setAiPdfFile] = useState<File | null>(null);
  const [aiExtracting, setAiExtracting] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiLoadingMsgIdx, setAiLoadingMsgIdx] = useState(0);
  const aiPdfInputRef = useRef<HTMLInputElement>(null);
  const aiLoadingIntervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (aiExtracting) {
      setAiLoadingMsgIdx(0);
      aiLoadingIntervalRef.current = setInterval(() => {
        setAiLoadingMsgIdx((i) => (i + 1) % AI_LOADING_MSGS.length);
      }, 4000);
    } else {
      clearInterval(aiLoadingIntervalRef.current);
    }
    return () => clearInterval(aiLoadingIntervalRef.current);
  }, [aiExtracting]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── AI bid analysis
  const [bidAnalysis, setBidAnalysis] = useState<BidAnalysis | null>(null);
  const [bidAnalyzing, setBidAnalyzing] = useState(false);
  const [bidAnalysisError, setBidAnalysisError] = useState('');

  // ── Advance stage modal
  const [advanceModalOpen, setAdvanceModalOpen] = useState(false);
  const [advanceDirection, setAdvanceDirection] = useState<'forward' | 'back'>('forward');
  const [advanceNotes, setAdvanceNotes] = useState('');
  const [advanceSaving, setAdvanceSaving] = useState(false);

  // ── Approval email modal
  const [approvalEmailOpen, setApprovalEmailOpen] = useState(false);
  const [approvalTo, setApprovalTo] = useState('');
  const [approvalCc, setApprovalCc] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalSending, setApprovalSending] = useState(false);
  const [approvalToFocused, setApprovalToFocused] = useState(false);
  const [approvalCcFocused, setApprovalCcFocused] = useState(false);
  const savedEmailsRef = useRef<string[]>(getSavedEmails());
  const [approvalPdfIds, setApprovalPdfIds] = useState<Set<number>>(new Set());
  const [quoteFileUploading, setQuoteFileUploading] = useState<Set<number>>(new Set());
  const [additionalFiles, setAdditionalFiles] = useState<File[]>([]);
  const [approvalUploadProgress, setApprovalUploadProgress] = useState('');
  const [approvalIsDragging, setApprovalIsDragging] = useState(false);
  const additionalFilesInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!approvalEmailOpen) return;
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setApprovalEmailOpen(false); };
    window.addEventListener('keydown', handler);
    return () => { window.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [approvalEmailOpen]);

  // ── Subcontracts
  const [subcontracts, setSubcontracts] = useState<Record<number, Subcontract[]>>({});
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [addSubQuoteId, setAddSubQuoteId] = useState<number | null>(null);
  const [subTab, setSubTab] = useState<'pdf' | 'existing' | 'manual'>('manual');
  const [subForm, setSubForm] = useState<SubcontractForm>(EMPTY_SUB_FORM);
  const [subPdfFile, setSubPdfFile] = useState<File | null>(null);
  const [subExtracting, setSubExtracting] = useState(false);
  const [subExtractError, setSubExtractError] = useState('');
  const [subSaving, setSubSaving] = useState(false);
  const [subError, setSubError] = useState('');
  const [expandedSubIds, setExpandedSubIds] = useState<Set<number>>(new Set());
  const subPdfInputRef = useRef<HTMLInputElement>(null);

  const mouseDownTarget = useRef<EventTarget | null>(null);

  // ── Edit subcontract modal
  const [editSubOpen, setEditSubOpen] = useState(false);
  const [editSubQuoteId, setEditSubQuoteId] = useState<number | null>(null);
  const [editSubId, setEditSubId] = useState<number | null>(null);
  const [editSubForm, setEditSubForm] = useState<SubcontractForm>(EMPTY_SUB_FORM);
  const [editSubSaving, setEditSubSaving] = useState(false);
  const [editSubError, setEditSubError] = useState('');

  // ── Toast
  const { toast, showToast } = useToast();

  // ── PDF viewer
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [confirmRemovePdf, setConfirmRemovePdf] = useState<number | null>(null);

  async function handleRemoveBidPdf(bidId: number) {
    try {
      await api.patch(`/projects/bids/${bidId}/remove-pdf`, {});
      setSelectedQuotes((prev) => prev.map((b) => b.id === bidId ? { ...b, file_url: null } : b));
      setConfirmRemovePdf(null);
    } catch {
      showToast('Failed to remove PDF', true);
    }
  }

  // ── Completed date input (shown when advancing to 'completed')
  const [completedDateInput, setCompletedDateInput] = useState(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  });

  // ── Scheduling date
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);

  async function handleSaveScheduledDate() {
    if (!selectedProject) return;
    setSavingSchedule(true);
    try {
      await api.put(`/projects/${selectedProject.id}/schedule-date`, { scheduled_date: scheduledDate || null });
      setSelectedProject({ ...selectedProject, scheduled_date: scheduledDate || null, scheduled_date_closed: false });
      qc.invalidateQueries({ queryKey: ['projects'] });
      setShowDatePicker(false);
      showToast('Scheduled date saved & added to calendar ✓');
    } catch {
      showToast('Failed to save scheduled date', true);
    } finally {
      setSavingSchedule(false);
    }
  }

  // ── Data fetching ──────────────────────────────────────────────────────────

  const { data: projectsRes, isLoading } = useQuery({
    queryKey: ['projects', statusFilter],
    queryFn: () =>
      api.get<Project[]>('/projects', statusFilter !== 'all' ? { status: statusFilter } : {}),
  });
  const projects: Project[] = projectsRes?.data ?? [];

  const { data: vendorsRes } = useQuery({
    queryKey: ['vendors-list'],
    queryFn: () => api.get<Vendor[]>('/vendors'),
  });
  const vendors: Vendor[] = vendorsRes?.data ?? [];

  // ── Filtered list ──────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = projects;
    // Workflow tab filter
    if (workflowTab === 'active') {
      list = list.filter((p) => p.workflow_status !== 'completed' && p.workflow_status !== 'paid');
    } else {
      list = list.filter((p) => p.workflow_status === 'completed' || p.workflow_status === 'paid');
    }
    const q = search.toLowerCase();
    if (!q) return list;
    return list.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      (p.description ?? '').toLowerCase().includes(q) ||
      (p.vendor_name ?? '').toLowerCase().includes(q)
    );
  }, [projects, search, workflowTab]);

  // ── Fetch quotes for selected project ─────────────────────────────────────

  async function fetchProjectQuotes(projectId: number) {
    setQuotesLoading(true);
    try {
      const res = await api.get<Quote[]>(`/projects/${projectId}/quotes`);
      const list = (res as unknown as { data: Quote[] }).data ?? [];
      setSelectedQuotes(list);
      // Fetch subcontracts for each quote
      for (const q of list) {
        void fetchSubcontractsForQuote(projectId, q.id);
      }
    } catch { setSelectedQuotes([]); }
    finally { setQuotesLoading(false); }
  }

  async function fetchSubcontractsForQuote(projectId: number, quoteId: number) {
    try {
      const res = await api.get<Subcontract[]>(`/projects/${projectId}/quotes/${quoteId}/subcontracts`);
      const list = (res as unknown as { data: Subcontract[] }).data ?? [];
      setSubcontracts((prev) => ({ ...prev, [quoteId]: list }));
    } catch { /* ignore */ }
  }

  async function handleAddSubcontract() {
    if (!selectedProject || !addSubQuoteId) return;
    if (!subForm.vendor_name.trim()) { setSubError('Vendor name is required'); return; }
    setSubSaving(true); setSubError('');
    try {
      await api.post(`/projects/${selectedProject.id}/quotes/${addSubQuoteId}/subcontracts`, {
        vendor_name: subForm.vendor_name,
        description: subForm.description || null,
        amount: subForm.amount ? parseFloat(subForm.amount) : null,
        line_items: subForm.line_items,
        notes: subForm.notes || null,
      });
      await fetchSubcontractsForQuote(selectedProject.id, addSubQuoteId);
      setAddSubOpen(false);
      setSubForm(EMPTY_SUB_FORM);
      setSubPdfFile(null);
      setSubTab('manual');
      showToast('Subcontract added');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setSubError(msg || 'Failed to save subcontract.');
    } finally { setSubSaving(false); }
  }

  async function handleDeleteSubcontract(quoteId: number, subId: number) {
    if (!selectedProject) return;
    try {
      await api.delete(`/projects/${selectedProject.id}/quotes/${quoteId}/subcontracts/${subId}`);
      setSubcontracts((prev) => ({ ...prev, [quoteId]: (prev[quoteId] ?? []).filter((s) => s.id !== subId) }));
    } catch { /* ignore */ }
  }

  async function handleUpdateSubcontract() {
    if (!selectedProject || !editSubQuoteId || !editSubId) return;
    if (!editSubForm.vendor_name.trim()) { setEditSubError('Vendor name is required'); return; }
    setEditSubSaving(true); setEditSubError('');
    try {
      await api.put(`/projects/${selectedProject.id}/quotes/${editSubQuoteId}/subcontracts/${editSubId}`, {
        vendor_name: editSubForm.vendor_name,
        description: editSubForm.description || null,
        amount: editSubForm.amount ? parseFloat(editSubForm.amount) : null,
        line_items: editSubForm.line_items,
        notes: editSubForm.notes || null,
      });
      await fetchSubcontractsForQuote(selectedProject.id, editSubQuoteId);
      setEditSubOpen(false);
      setEditSubForm(EMPTY_SUB_FORM);
      showToast('Subcontract updated');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setEditSubError(msg || 'Failed to update subcontract.');
    } finally { setEditSubSaving(false); }
  }

  async function handleSubAiExtract() {
    if (!subPdfFile || !selectedProject) return;
    setSubExtracting(true); setSubExtractError('');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);
    try {
      const formData = new FormData();
      formData.append('pdf', subPdfFile);
      const token = localStorage.getItem('accessToken');
      const baseUrl = (import.meta.env.VITE_API_URL as string) || apiClient.defaults.baseURL || '/api';
      const response = await fetch(`${baseUrl}/projects/${selectedProject.id}/extract-proposal`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        const d = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? `Server error ${response.status}`);
      }
      const result = await response.json() as { success: boolean; data?: { vendor_name?: string; total_cost?: number; project_scope?: string; line_items?: unknown[] } };
      const d = result.data;
      if (d) {
        setSubForm((f) => ({
          ...f,
          vendor_name: d.vendor_name ?? f.vendor_name,
          amount: d.total_cost ? String(d.total_cost) : f.amount,
          description: d.project_scope ?? f.description,
          line_items: Array.isArray(d.line_items) ? (d.line_items as QuoteLineItem[]) : f.line_items,
        }));
        showToast('Extracted — review fields before saving');
      }
    } catch (err) {
      clearTimeout(timeoutId);
      const msg = err instanceof Error ? err.message : 'Extraction failed';
      setSubExtractError(msg);
    } finally { setSubExtracting(false); }
  }

  async function handleQuoteFileUpload(quoteId: number, file: File) {
    if (!selectedProject) return;
    setQuoteFileUploading((prev) => new Set([...prev, quoteId]));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.patch(`/projects/${selectedProject.id}/quotes/${quoteId}/file`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }) as { data?: { data?: Quote } };
      const updated = res?.data?.data;
      if (updated) {
        setSelectedQuotes((prev) => prev.map((q) => q.id === quoteId ? { ...q, ...updated } : q));
        setApprovalPdfIds((prev) => new Set([...prev, quoteId]));
      }
    } catch {
      showToast('Upload failed', true);
    } finally {
      setQuoteFileUploading((prev) => { const next = new Set(prev); next.delete(quoteId); return next; });
    }
  }

  async function handleAnalyzeBids() {
    if (!selectedProject) return;
    setBidAnalyzing(true);
    setBidAnalysisError('');
    setBidAnalysis(null);
    try {
      // Rule 8: AI analysis may take >10s — use VITE_API_URL directly (not Vercel proxy)
      const token = localStorage.getItem('accessToken');
      const baseUrl = import.meta.env.VITE_API_URL ?? '';
      const res = await fetch(`${baseUrl}/projects/${selectedProject.id}/analyze-bids`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? `Server error ${res.status}`);
      }
      const d = await res.json() as { success: boolean; data: BidAnalysis };
      setBidAnalysis(d.data);
      // Persist analysis to project
      try {
        await apiClient.patch(`/projects/${selectedProject.id}/ai-analysis`, { ai_analysis: d.data });
        setSelectedProject((p) => p ? { ...p, ai_analysis: d.data } : p);
      } catch { /* non-critical */ }
    } catch (err) {
      setBidAnalysisError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setBidAnalyzing(false);
    }
  }

  async function handleAdvanceStage() {
    if (!selectedProject) return;
    const currentIdx = getWorkflowIndex(selectedProject.workflow_status);
    const targetIdx = advanceDirection === 'forward' ? currentIdx + 1 : currentIdx - 1;
    if (targetIdx < 0 || targetIdx >= WORKFLOW_STAGES.length) return;
    const targetStatus = WORKFLOW_STAGES[targetIdx].key;
    setAdvanceSaving(true);
    try {
      const res = await apiClient.patch(`/projects/${selectedProject.id}/workflow-status`, {
        workflow_status: targetStatus,
        notes: advanceNotes || undefined,
        ...(targetStatus === 'completed' ? { completed_date: completedDateInput } : {}),
      });
      const updated = res.data?.data;
      setSelectedProject((p) => p ? { ...p, ...updated, workflow_status: updated?.workflow_status ?? targetStatus } : p);
      // Immediately patch the cached projects list so the table reflects vendor/cost changes
      if (updated?.id) {
        qc.setQueryData(['projects', statusFilter], (old: { data: Project[] } | undefined) => {
          if (!old?.data) return old;
          return { ...old, data: old.data.map((p: Project) => p.id === updated.id ? { ...p, ...updated } : p) };
        });
      }
      qc.invalidateQueries({ queryKey: ['projects'] });
      setAdvanceModalOpen(false);
      setAdvanceNotes('');
      const _t = new Date();
      setCompletedDateInput(`${_t.getFullYear()}-${String(_t.getMonth() + 1).padStart(2, '0')}-${String(_t.getDate()).padStart(2, '0')}`);
      showToast(`Stage advanced to: ${getWorkflowLabel(targetStatus)}`);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showToast(msg ?? 'Failed to update stage', true);
    } finally {
      setAdvanceSaving(false);
    }
  }

  async function handleSendApprovalEmail() {
    if (!selectedProject) return;
    if (!approvalTo.trim()) { showToast('Recipient email is required', true); return; }
    setApprovalSending(true);

    // Upload additional files first (Rule 8: use VITE_API_URL for uploads)
    const additionalDocumentUrls: Array<{ url: string; filename: string; format: string }> = [];
    if (additionalFiles.length > 0) {
      const baseUrl = (import.meta.env.VITE_API_URL as string) || apiClient.defaults.baseURL || '/api';
      const token = localStorage.getItem('accessToken');
      for (let i = 0; i < additionalFiles.length; i++) {
        setApprovalUploadProgress(`Uploading ${i + 1} of ${additionalFiles.length} files…`);
        const fd = new FormData();
        fd.append('file', additionalFiles[i]);
        try {
          const uploadRes = await fetch(`${baseUrl}/projects/${selectedProject.id}/upload-approval-document`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
          if (!uploadRes.ok) throw new Error(`Upload failed for ${additionalFiles[i].name}`);
          const uploadData = await uploadRes.json() as { url: string; filename: string; format: string };
          additionalDocumentUrls.push({ url: uploadData.url, filename: uploadData.filename, format: uploadData.format });
        } catch {
          showToast(`Failed to upload ${additionalFiles[i].name}`, true);
          setApprovalSending(false);
          setApprovalUploadProgress('');
          return;
        }
      }
      setApprovalUploadProgress('Sending email…');
    }

    const attachedPdfs = selectedQuotes
      .filter((q) => q.file_url && approvalPdfIds.has(q.id))
      .map((q) => ({ vendor_name: q.vendor_name, file_url: q.file_url!, file_name: q.file_name ?? null, file_type: q.file_type ?? null }));
    try {
      await apiClient.post(`/projects/${selectedProject.id}/send-approval-email`, {
        to: approvalTo.trim(),
        cc: approvalCc.trim() || undefined,
        notes: approvalNotes.trim() || undefined,
        attachedPdfs: attachedPdfs.length > 0 ? attachedPdfs : undefined,
        additionalDocumentUrls: additionalDocumentUrls.length > 0 ? additionalDocumentUrls : undefined,
      });
      setSelectedProject((p) => p ? { ...p, workflow_status: 'approval_sent' } : p);
      qc.invalidateQueries({ queryKey: ['projects'] });
      // Save emails to localStorage history
      if (approvalTo.trim()) saveApprovalEmail(approvalTo.trim());
      approvalCc.split(',').map(s => s.trim()).filter(Boolean).forEach(saveApprovalEmail);
      savedEmailsRef.current = getSavedEmails();
      setApprovalEmailOpen(false);
      setAdditionalFiles([]);
      setApprovalUploadProgress('');
      showToast('Approval email sent');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showToast(msg ?? 'Failed to send email', true);
    } finally {
      setApprovalSending(false);
      setApprovalUploadProgress('');
    }
  }

  function openProject(p: Project) {
    setSelectedProject(p);
    setSelectedQuotes([]);
    fetchProjectQuotes(p.id);
    setAddBidOpen(false);
    setQuoteForm(EMPTY_QUOTE_FORM);
    setQuoteFile(null);
    setQuoteError('');
    // Load saved AI analysis if available
    setBidAnalysis(p.ai_analysis ?? null);
    setBidAnalysisError('');
    setAdvanceModalOpen(false);
    setAdvanceNotes('');
    setApprovalEmailOpen(false);
    setApprovalTo('');
    setApprovalCc('');
    setApprovalNotes('');
    setApprovalPdfIds(new Set());
  }

  function closeProject() {
    setSelectedProject(null);
    setSelectedQuotes([]);
    setAddBidOpen(false);
  }

  // ── Panel helpers ──────────────────────────────────────────────────────────

  function openCreate() {
    setEditing(null); setForm(EMPTY_FORM); setError(''); setVendorSearch(''); setVendorDropdownOpen(false); setPanelOpen(true);
  }
  function openEdit(p: Project, e?: React.MouseEvent) {
    e?.stopPropagation();
    setEditing(p); setForm(projectToForm(p)); setError('');
    setVendorSearch(vendors.find(v => String(v.id) === String(p.vendor_id))?.name ?? '');
    setVendorDropdownOpen(false);
    setPanelOpen(true);
  }
  function closePanel() {
    setPanelOpen(false); setEditing(null); setError(''); setVendorSearch(''); setVendorDropdownOpen(false);
  }

  // ── Line items ─────────────────────────────────────────────────────────────

  function addLineItem() {
    setForm((f) => ({ ...f, line_items: [...f.line_items, { description: '', quantity: 1, unit_cost: 0 }] }));
  }
  function updateLineItem(idx: number, field: keyof LineItem, value: string | number) {
    setForm((f) => { const items = [...f.line_items]; items[idx] = { ...items[idx], [field]: value }; return { ...f, line_items: items }; });
  }
  function removeLineItem(idx: number) {
    setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));
  }

  // ── Save project ───────────────────────────────────────────────────────────

  async function handleSave() {
    if (!form.name.trim()) { setError('Project name is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        name: form.name.trim(), description: form.description || null, status: form.status,
        vendor_id: form.vendor_id ? parseInt(form.vendor_id) : null,
        estimated_cost: form.estimated_cost ? parseFloat(form.estimated_cost) : null,
        actual_cost: form.actual_cost ? parseFloat(form.actual_cost) : null,
        start_date: form.start_date || null, end_date: form.end_date || null, notes: form.notes || null,
        line_items: form.line_items.filter((li) => li.description.trim())
          .map((li) => ({ description: li.description, quantity: li.quantity || 1, unit_cost: li.unit_cost || 0 })),
      };
      if (editing) {
        await api.put(`/projects/${editing.id}`, payload);
      } else {
        await api.post('/projects', payload);
      }
      qc.invalidateQueries({ queryKey: ['projects'] });
      closePanel();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Failed to save project.');
    } finally { setSaving(false); }
  }

  // ── Delete project ─────────────────────────────────────────────────────────

  async function handleDelete(p: Project) {
    try {
      await api.delete(`/projects/${p.id}`);
      qc.invalidateQueries({ queryKey: ['projects'] });
      setDeleteConfirm(null);
      if (selectedProject?.id === p.id) closeProject();
    } catch { /* server error already logged */ }
  }

  // ── Add bid ────────────────────────────────────────────────────────────────

  async function handleAddQuote() {
    if (!selectedProject) return;
    setQuoteSaving(true); setQuoteError('');
    try {
      const fd = new FormData();
      if (quoteForm.vendor_name) fd.append('vendor_name', quoteForm.vendor_name);
      if (quoteForm.vendor_contact) fd.append('vendor_contact', quoteForm.vendor_contact);
      if (quoteForm.vendor_email) fd.append('vendor_email', quoteForm.vendor_email);
      if (quoteForm.vendor_phone) fd.append('vendor_phone', quoteForm.vendor_phone);
      if (quoteForm.quote_amount) fd.append('quote_amount', quoteForm.quote_amount);
      if (quoteForm.notes) fd.append('notes', quoteForm.notes);
      if (quoteForm.line_items.length > 0) fd.append('line_items', JSON.stringify(quoteForm.line_items));
      if (quoteFile) fd.append('file', quoteFile);

      await apiClient.post(`/projects/${selectedProject.id}/quotes`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setQuoteForm(EMPTY_QUOTE_FORM);
      setQuoteFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setAddBidOpen(false);
      await fetchProjectQuotes(selectedProject.id);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setQuoteError(msg || 'Failed to add bid.');
    } finally { setQuoteSaving(false); }
  }

  async function handleDeleteQuote(quoteId: number) {
    if (!selectedProject) return;
    try {
      await api.delete(`/projects/${selectedProject.id}/quotes/${quoteId}`);
      await fetchProjectQuotes(selectedProject.id);
    } catch { /* ignore */ }
  }

  async function handleRecommend(quoteId: number) {
    try {
      await apiClient.patch(`/projects/quotes/${quoteId}/recommend`);
      await fetchProjectQuotes(selectedProject!.id);
    } catch { /* ignore */ }
  }

  function openEditQuote(q: Quote) {
    setEditingQuote(q);
    setEditQuoteForm({
      vendor_name: q.vendor_name ?? '',
      vendor_contact: q.vendor_contact ?? '',
      vendor_email: q.vendor_email ?? '',
      vendor_phone: q.vendor_phone ?? '',
      quote_amount: q.quote_amount ?? '',
      notes: q.notes ?? '',
      line_items: Array.isArray(q.line_items) ? q.line_items : [],
    });
    setEditQuoteError('');
    setEditBidOpen(true);
  }

  async function handleUpdateQuote() {
    if (!selectedProject || !editingQuote) return;
    setEditQuoteSaving(true); setEditQuoteError('');
    try {
      await apiClient.put(`/projects/${selectedProject.id}/quotes/${editingQuote.id}`, {
        vendor_name: editQuoteForm.vendor_name || null,
        vendor_contact: editQuoteForm.vendor_contact || null,
        vendor_email: editQuoteForm.vendor_email || null,
        vendor_phone: editQuoteForm.vendor_phone || null,
        quote_amount: editQuoteForm.quote_amount ? parseFloat(editQuoteForm.quote_amount) : null,
        notes: editQuoteForm.notes || null,
        line_items: editQuoteForm.line_items,
      });
      setEditBidOpen(false);
      setEditingQuote(null);
      await fetchProjectQuotes(selectedProject.id);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setEditQuoteError(msg || 'Failed to update bid.');
    } finally { setEditQuoteSaving(false); }
  }

  // ── AI extraction ──────────────────────────────────────────────────────────

  async function handleAiExtract() {
    if (!aiPdfFile || !selectedProject) return;
    setAiExtracting(true); setAiError('');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);
    try {
      const formData = new FormData();
      formData.append('pdf', aiPdfFile);
      const token = localStorage.getItem('accessToken');
      const baseUrl = (import.meta.env.VITE_API_URL as string) || apiClient.defaults.baseURL || '/api';
      const response = await fetch(`${baseUrl}/projects/${selectedProject.id}/extract-proposal`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        const d = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? `Server error ${response.status}`);
      }
      const result = await response.json() as { success: boolean; data?: { vendor_name?: string; vendor_contact?: string; vendor_email?: string; vendor_phone?: string; total_cost?: number; project_scope?: string; line_items?: unknown[] } };
      const d = result.data;
      if (d) {
        setQuoteForm({
          vendor_name: d.vendor_name ?? '',
          vendor_contact: d.vendor_contact ?? '',
          vendor_email: d.vendor_email ?? '',
          vendor_phone: d.vendor_phone ?? '',
          quote_amount: d.total_cost ? String(d.total_cost) : '',
          notes: d.project_scope ?? '',
          line_items: Array.isArray(d.line_items) ? (d.line_items as QuoteLineItem[]) : [],
        });
        setAiModalOpen(false);
        setAiPdfFile(null);
        setAddBidOpen(true);
        showToast('Proposal extracted successfully — please review before saving');
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof Error && err.name === 'AbortError') {
        const msg = 'Request timed out — the PDF may be too complex. Please try again.';
        setAiError(msg);
        showToast(msg, true);
      } else {
        const errMsg = err instanceof Error ? err.message : 'Extraction failed. Please try again.';
        setAiError(errMsg);
        showToast(errMsg, true);
      }
    } finally {
      setAiExtracting(false);
    }
  }

  // ── Detail view ────────────────────────────────────────────────────────────

  if (selectedProject) {
    const sc = STATUS_CONFIG[selectedProject.status];
    return (
      <div>
        {/* Toast */}
        <Toast toast={toast} />
        {/* Demo-mode toast */}
        {demoToast && (
          <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#b45309', color: '#fff', borderRadius: 8, padding: '10px 20px', fontSize: '0.875rem', fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
            ⚠ {demoToast}
          </div>
        )}

        {/* PDF viewer portal */}
        {pdfViewerUrl && ReactDOM.createPortal(
          <BidPdfViewer url={pdfViewerUrl} onClose={() => setPdfViewerUrl(null)} />,
          document.body
        )}

        {/* Page header */}
        <div className="page-header" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="btn btn-ghost"
              onClick={closeProject}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px' }}
            >
              <ArrowLeft size={16} /> Back
            </button>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h1 className="page-title" style={{ margin: 0 }}>{selectedProject.name}</h1>
                <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700, color: sc.color, background: sc.bg }}>
                  {sc.label}
                </span>
              </div>
              {selectedProject.notes && (
                <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>{selectedProject.notes}</p>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {selectedProject.workflow_status === 'obtaining_bids' && selectedQuotes.length >= 1 && (
              <button
                className="btn btn-ghost"
                style={{ color: 'var(--accent-primary)', borderColor: 'rgba(59,130,246,0.35)', display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={() => {
                  setApprovalEmailOpen(true);
                  setApprovalTo('');
                  setApprovalCc('');
                  setApprovalNotes('');
                  setAdditionalFiles([]);
                  setApprovalUploadProgress('');
                  setApprovalPdfIds(new Set(selectedQuotes.filter((q) => q.file_url).map((q) => q.id)));
                }}
              >
                <Send size={14} /> Send for Approval
              </button>
            )}
            {(() => {
              const ci = getWorkflowIndex(selectedProject.workflow_status);
              const canForward = ci < WORKFLOW_STAGES.length - 1;
              const canBack = ci > 0;
              return (
                <>
                  {canBack && (
                    <button
                      className="btn btn-ghost"
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      onClick={() => { setAdvanceDirection('back'); setAdvanceNotes(''); setAdvanceModalOpen(true); }}
                    >
                      <ChevronLeft size={14} /> Go Back
                    </button>
                  )}
                  {selectedProject.workflow_status === 'scheduling' && (
                    <button
                      className="btn btn-ghost"
                      style={{ color: 'var(--accent-warning)', borderColor: 'rgba(245,158,11,0.35)', display: 'flex', alignItems: 'center', gap: 6 }}
                      onClick={() => {
                        setScheduledDate((selectedProject.scheduled_date ?? '').slice(0, 10));
                        setShowDatePicker(true);
                      }}
                    >
                      📅 Set Scheduled Date
                    </button>
                  )}
                  {canForward && (
                    <button
                      className="btn btn-ghost"
                      style={{ color: 'var(--accent-primary)', borderColor: 'rgba(59,130,246,0.35)', display: 'flex', alignItems: 'center', gap: 6 }}
                      onClick={() => { setAdvanceDirection('forward'); setAdvanceNotes(''); setAdvanceModalOpen(true); }}
                    >
                      Advance Stage <ChevronRight size={14} />
                    </button>
                  )}
                </>
              );
            })()}
            <button className="btn btn-ghost" onClick={() => demoBlock(() => openEdit(selectedProject))}>
              <Pencil size={14} /> Edit Project
            </button>
            {selectedQuotes.length < 3 && (
              <button className="btn btn-primary" onClick={() => demoBlock(() => { setAddBidOpen(true); setQuoteForm(EMPTY_QUOTE_FORM); setQuoteFile(null); setQuoteError(''); })}>
                <Plus size={16} /> Add Bid
              </button>
            )}
          </div>
        </div>

        {/* Scheduling date banner */}
        {selectedProject.workflow_status === 'scheduling' && (selectedProject.scheduled_date || showDatePicker) && (
          <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {showDatePicker ? (
              <>
                <span style={{ fontSize: '0.875rem', color: 'var(--accent-warning)', fontWeight: 600 }}>📅 Scheduled Date:</span>
                <input
                  type="date"
                  className="form-control"
                  style={{ width: 180 }}
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                />
                <button className="btn btn-primary" style={{ padding: '4px 14px', fontSize: '0.8125rem' }} onClick={handleSaveScheduledDate} disabled={savingSchedule}>
                  {savingSchedule ? 'Saving…' : 'Confirm Date'}
                </button>
                <button className="btn btn-ghost" style={{ padding: '4px 14px', fontSize: '0.8125rem' }} onClick={() => setShowDatePicker(false)}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span style={{ fontSize: '0.875rem', color: 'var(--accent-warning)', fontWeight: 600 }}>
                  Scheduled: {selectedProject.scheduled_date
                    ? (() => { const _d = new Date(selectedProject.scheduled_date!); return new Date(_d.getUTCFullYear(), _d.getUTCMonth(), _d.getUTCDate()).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); })()
                    : '—'}
                </span>
                <button
                  className="btn btn-ghost"
                  style={{ padding: '2px 8px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={() => { setScheduledDate((selectedProject.scheduled_date ?? '').slice(0, 10)); setShowDatePicker(true); }}
                >
                  <Pencil size={12} /> Edit
                </button>
              </>
            )}
          </div>
        )}

        {/* Workflow stepper */}
        {(() => {
          const ci = getWorkflowIndex(selectedProject.workflow_status);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 20, overflowX: 'auto', paddingBottom: 4 }}>
              {WORKFLOW_STAGES.map((stage, idx) => {
                const isCompleted = idx < ci;
                const isCurrent = idx === ci;
                const color = isCompleted ? 'var(--accent-success)' : isCurrent ? 'var(--accent-primary)' : 'var(--text-muted)';
                const bg = isCompleted ? 'rgba(16,185,129,0.15)' : isCurrent ? 'rgba(59,130,246,0.15)' : 'var(--bg-surface)';
                const border = isCompleted ? '1px solid rgba(16,185,129,0.4)' : isCurrent ? '1px solid rgba(59,130,246,0.5)' : '1px solid var(--border-subtle)';
                return (
                  <div key={stage.key} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{
                      padding: '5px 12px', borderRadius: 999, fontSize: '0.75rem', fontWeight: isCurrent ? 700 : 500,
                      color, background: bg, border, whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}>
                      {isCompleted && <Check size={11} />}
                      {stage.label}
                    </div>
                    {idx < WORKFLOW_STAGES.length - 1 && (
                      <div style={{ width: 20, height: 1, background: idx < ci ? 'var(--accent-success)' : 'var(--border-subtle)', flexShrink: 0 }} />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Bid columns */}
        {quotesLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div className="loading-spinner" />
          </div>
        ) : selectedQuotes.length === 0 ? (
          <div className="empty-state">
            <FolderKanban size={44} />
            <h3>No bids yet</h3>
            <p>Click Add Bid to get started</p>
            <button className="btn btn-primary" onClick={() => setAddBidOpen(true)}>
              <Plus size={16} /> Add Bid
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', paddingBottom: 16 }}>
            <div style={{
              display: 'flex', gap: 16,
              minWidth: `${(selectedQuotes.length + 1) * 296}px`,
            }}>
              {selectedQuotes.map((q) => {
                const isRecommended = q.is_recommended;
                const qSubs = subcontracts[q.id] ?? [];
                const bidAmt = q.quote_amount ? parseFloat(q.quote_amount) : null;
                const subTotal = qSubs.reduce((s, sub) => s + (sub.amount ? parseFloat(sub.amount) : 0), 0);
                const grandTotal = bidAmt !== null ? bidAmt + subTotal : null;
                return (
                  <div key={q.id} style={{ flex: '0 0 280px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 10 }}>

                    {/* ─── Total cost summary card ────────────────────── */}
                    <div style={{
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px 16px',
                    }}>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                        Grand Total
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--accent-warning)', lineHeight: 1 }}>
                        {grandTotal !== null ? fmtCurrency(grandTotal) : '—'}
                      </div>
                    </div>

                    {/* ─── Bid card ───────────────────────────────────── */}
                    <div
                      className="card"
                      style={{
                        display: 'flex', flexDirection: 'column',
                        gap: 12, position: 'relative',
                        ...(isRecommended ? { borderColor: 'var(--accent-success)' } : {}),
                        transition: 'border-color 150ms ease, box-shadow 150ms ease',
                      }}
                      onMouseEnter={(e) => { if (!isRecommended) { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-default)'; (e.currentTarget as HTMLDivElement).style.boxShadow = 'var(--shadow-sm)'; }}}
                      onMouseLeave={(e) => { if (!isRecommended) { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-subtle)'; (e.currentTarget as HTMLDivElement).style.boxShadow = 'none'; }}}
                    >
                    {/* Vendor name + recommended badge */}
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', lineHeight: 1.3 }}>
                        {q.vendor_name || '(No vendor)'}
                      </div>
                      {isRecommended && (
                        <span style={{
                          display: 'inline-block', marginTop: 6,
                          padding: '2px 8px', borderRadius: 999,
                          fontSize: '0.6875rem', fontWeight: 600,
                          background: 'rgba(16,185,129,0.15)', color: 'var(--accent-success)',
                          border: '1px solid rgba(16,185,129,0.3)',
                        }}>
                          ★ Recommended
                        </span>
                      )}
                    </div>

                    {/* Contact info with icons */}
                    {(q.vendor_contact || q.vendor_email || q.vendor_phone) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                        {q.vendor_contact && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{q.vendor_contact}</span>
                          </div>
                        )}
                        {q.vendor_email && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <Mail size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                            <a href={`mailto:${q.vendor_email}`} onClick={(e) => e.stopPropagation()} style={{ color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.vendor_email}</a>
                          </div>
                        )}
                        {q.vendor_phone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <Phone size={13} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                            <a href={`tel:${q.vendor_phone}`} onClick={(e) => e.stopPropagation()} style={{ color: 'inherit' }}>{q.vendor_phone}</a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Quote amount */}
                    <div style={{
                      fontSize: '1.75rem', fontWeight: 800,
                      color: q.quote_amount ? 'var(--accent-success)' : 'var(--text-muted)',
                      lineHeight: 1,
                    }}>
                      {q.quote_amount ? fmtCurrency(q.quote_amount) : 'No amount'}
                    </div>

                    {/* Line items table */}
                    {q.line_items && q.line_items.length > 0 && (
                      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 6, overflow: 'hidden' }}>
                        <div style={{
                          display: 'grid', gridTemplateColumns: '45% 10% 22% 23%',
                          padding: '5px 8px', background: 'var(--bg-surface)',
                          fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase',
                        }}>
                          <span>Item</span><span>Qty</span><span>Unit Price</span><span>Total</span>
                        </div>
                        {q.line_items.map((li, i) => (
                          <div key={i} style={{
                            display: 'grid', gridTemplateColumns: '45% 10% 22% 23%',
                            padding: '5px 8px', fontSize: '0.75rem', color: 'var(--text-secondary)',
                            borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)',
                          }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{li.description}</span>
                            <span>{li.quantity}</span>
                            <span>{fmtCurrency(li.unit_price)}</span>
                            <span>{fmtCurrency(li.total)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Notes */}
                    {q.notes && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        {q.notes}
                      </div>
                    )}

                    {/* PDF link + Remove PDF */}
                    {q.file_url && (
                      confirmRemovePdf === q.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Remove this PDF?</span>
                          <button className="btn btn-sm" style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', fontSize: 11 }}
                            onClick={() => handleRemoveBidPdf(q.id)}>Confirm</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setConfirmRemovePdf(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span
                            style={{ cursor: 'pointer', color: '#818cf8', fontSize: 13, textDecoration: 'none' }}
                            onClick={() => setPdfViewerUrl(q.file_url)}
                          >
                            <FileText size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                            View {q.file_type?.toUpperCase() ?? 'PDF'} document
                          </span>
                          <button
                            onClick={() => setConfirmRemovePdf(q.id)}
                            style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', color: '#ef4444', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          >
                            ✕ Remove PDF
                          </button>
                        </div>
                      )
                    )}

                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 'auto' }}>
                      Added {fmtDate(q.created_at)}
                    </div>

                    {/* Action footer: Edit (left) · Select (center) · Delete (right) */}
                    <div style={{ paddingTop: 12, borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEditQuote(q)}>
                        <Pencil size={13} /> Edit
                      </button>
                      <button
                        className={isRecommended ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                        onClick={() => handleRecommend(q.id)}
                      >
                        {isRecommended ? <Check size={13} /> : <Star size={13} />}
                        {isRecommended ? 'Selected' : 'Select'}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleDeleteQuote(q.id)}
                        style={{ color: 'var(--accent-danger)', borderColor: 'rgba(239,68,68,0.2)' }}
                        title="Remove bid"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    {/* ─── end bid card ───────────────────────────────── */}
                    </div>

                    {/* ─── Subcontracts section ───────────────────────── */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {qSubs.map((sub) => {
                        const isExp = expandedSubIds.has(sub.id);
                        return (
                          <div key={sub.id} style={{
                            background: 'var(--bg-surface)',
                            border: '1px solid var(--border-subtle)',
                            borderRadius: 'var(--radius-md)',
                            overflow: 'hidden',
                            fontSize: '0.8125rem',
                          }}>
                            {/* Header row */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px' }}>
                              <button
                                onClick={() => setExpandedSubIds((prev) => {
                                  const next = new Set(prev);
                                  next.has(sub.id) ? next.delete(sub.id) : next.add(sub.id);
                                  return next;
                                })}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                              >
                                {isExp ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                              </button>
                              <span style={{ fontWeight: 600, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {sub.vendor_name || '(No vendor)'}
                              </span>
                              {sub.amount && (
                                <span style={{ fontWeight: 700, color: 'var(--accent-warning)', flexShrink: 0 }}>
                                  {fmtCurrency(sub.amount)}
                                </span>
                              )}
                              <button
                                onClick={() => {
                                  setEditSubQuoteId(q.id);
                                  setEditSubId(sub.id);
                                  setEditSubForm({
                                    vendor_name: sub.vendor_name ?? '',
                                    description: sub.description ?? '',
                                    amount: sub.amount ?? '',
                                    notes: sub.notes ?? '',
                                    line_items: Array.isArray(sub.line_items) ? sub.line_items : [],
                                  });
                                  setEditSubError('');
                                  setEditSubOpen(true);
                                }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-primary)', padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                title="Edit subcontract"
                              >
                                <Pencil size={12} />
                              </button>
                              <button
                                onClick={() => handleDeleteSubcontract(q.id, sub.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-danger)', padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                title="Delete subcontract"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                            {/* Expanded body */}
                            {isExp && (
                              <div style={{ padding: '8px 10px', borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {sub.description && (
                                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', lineHeight: 1.4 }}>{sub.description}</div>
                                )}
                                {Array.isArray(sub.line_items) && sub.line_items.length > 0 && (
                                  <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 4, overflow: 'hidden' }}>
                                    {sub.line_items.map((li, i) => (
                                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 32px 60px', padding: '4px 6px', fontSize: '0.7rem', borderTop: i > 0 ? '1px solid var(--border-subtle)' : 'none', background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{li.description}</span>
                                        <span>{li.quantity}</span>
                                        <span>{fmtCurrency(li.unit_price)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {sub.notes && (
                                  <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontStyle: 'italic' }}>{sub.notes}</div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Add subcontract button */}
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ width: '100%', justifyContent: 'center', fontSize: '0.8rem', border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-md)', padding: '7px 0' }}
                        onClick={() => {
                          setAddSubQuoteId(q.id);
                          setSubForm(EMPTY_SUB_FORM);
                          setSubPdfFile(null);
                          setSubTab('manual');
                          setSubError('');
                          setSubExtractError('');
                          setAddSubOpen(true);
                        }}
                      >
                        <Plus size={12} /> Add Subcontract
                      </button>
                    </div>
                    {/* ─── end column ─────────────────────────────────── */}
                  </div>
                );
              })}

              {/* ─── AI Analysis Card ─────────────────────────────────── */}
              <div
                className="card"
                style={{
                  flex: '0 0 280px', minWidth: 280,
                  display: 'flex', flexDirection: 'column',
                  gap: 12, position: 'relative',
                  borderColor: bidAnalysis ? 'var(--accent-purple)' : undefined,
                  transition: 'border-color 150ms ease',
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: 'rgba(139,92,246,0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Brain size={15} style={{ color: 'var(--accent-purple)' }} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--accent-purple)', lineHeight: 1.3 }}>
                    AI Analysis
                  </div>
                </div>

                {selectedQuotes.length < 2 ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem', padding: '12px 0' }}>
                    Add at least 2 bids to enable AI analysis
                  </div>
                ) : bidAnalyzing ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '20px 0' }}>
                    <div className="loading-spinner" />
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>Analyzing bids…</span>
                  </div>
                ) : bidAnalysis ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                    {/* Best Price */}
                    <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent-success)', marginBottom: 4 }}>Best Price</div>
                      <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>{bidAnalysis.best_price.vendor}</div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--accent-success)', fontWeight: 600 }}>{fmtCurrency(bidAnalysis.best_price.amount)}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.4 }}>{bidAnalysis.best_price.reason}</div>
                    </div>

                    {/* Best Value */}
                    <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent-primary)', marginBottom: 4 }}>Best Value</div>
                      <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>{bidAnalysis.best_value.vendor}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.4 }}>{bidAnalysis.best_value.reason}</div>
                    </div>

                    {/* Risks */}
                    {bidAnalysis.risks.length > 0 && (
                      <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent-warning)', marginBottom: 6 }}>Risks</div>
                        {bidAnalysis.risks.map((r, i) => (
                          <div key={i} style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 4, lineHeight: 1.4 }}>
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{r.vendor}:</span> {r.risk}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Missing Info */}
                    {bidAnalysis.missing_info.length > 0 && (
                      <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent-danger)', marginBottom: 6 }}>Missing Info</div>
                        {bidAnalysis.missing_info.map((m, i) => (
                          <div key={i} style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 4, lineHeight: 1.4 }}>
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{m.vendor}:</span> {m.missing}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Recommendation */}
                    <div style={{ padding: '12px 14px', borderRadius: 8, background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)', marginTop: 'auto' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent-purple)', marginBottom: 6 }}>Recommendation</div>
                      <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--accent-purple)', marginBottom: 6 }}>{bidAnalysis.recommendation.vendor}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{bidAnalysis.recommendation.reasoning}</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '20px 0', textAlign: 'center' }}>
                    <Sparkles size={28} style={{ color: 'var(--accent-purple)', opacity: 0.5 }} />
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', lineHeight: 1.5 }}>
                      Let AI compare all bids and surface the best option
                    </div>
                    {bidAnalysisError && (
                      <div style={{ color: 'var(--accent-danger)', fontSize: '0.75rem', marginTop: 4 }}>
                        ⚠ {bidAnalysisError}
                      </div>
                    )}
                  </div>
                )}

                {/* Footer */}
                <div style={{ paddingTop: 12, borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  {selectedProject.ai_analysis && !bidAnalysis && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Analysis saved · {format(parseISO(selectedProject.updated_at), 'MMM d, yyyy')}</div>
                  )}
                  {selectedQuotes.length >= 2 && (
                    isEnterprise ? (
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ background: 'rgba(139,92,246,0.9)', borderColor: 'rgba(139,92,246,0.5)', display: 'flex', alignItems: 'center', gap: 6 }}
                        onClick={() => { void handleAnalyzeBids(); }}
                        disabled={bidAnalyzing}
                      >
                        <Sparkles size={13} />
                        {bidAnalysis ? 'Re-analyze' : 'Analyze Bids'}
                      </button>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <Lock size={16} style={{ color: 'var(--text-muted)' }} />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center' }}>Enterprise plan required for bid analysis</span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Bid modal */}
        {addBidOpen && ReactDOM.createPortal(
          <div className="modal-overlay" style={{ zIndex: 500 }}>
            <div className="modal" style={{ maxWidth: 520, width: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0 }}>Add Bid</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  {/* AI Import button — tier-gated */}
                  {isEnterprise ? (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--accent-primary)' }}
                      onClick={() => { setAiModalOpen(true); setAiPdfFile(null); setAiError(''); }}
                    >
                      <Sparkles size={14} /> Import Proposal with AI
                    </button>
                  ) : isPro ? (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--accent-primary)' }}
                      onClick={() => { setAiModalOpen(true); setAiPdfFile(null); setAiError(''); }}
                    >
                      <Sparkles size={14} /> Import Proposal with AI
                      <span style={{ fontSize: '0.7rem', background: 'rgba(139,92,246,0.12)', color: 'var(--accent-purple)', borderRadius: 10, padding: '1px 6px', marginLeft: 2 }}>
                        {aiUsed}/{aiLimit} used
                      </span>
                    </button>
                  ) : (
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', cursor: 'not-allowed', opacity: 0.6 }}
                      disabled
                      title="AI extraction requires Pro or Enterprise plan"
                    >
                      <Lock size={14} /> Import Proposal with AI
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm" onClick={() => setAddBidOpen(false)}><X size={16} /></button>
                </div>
              </div>

              {quoteError && (
                <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '0.875rem' }}>
                  <AlertCircle size={14} style={{ display: 'inline', marginRight: 6 }} />{quoteError}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Vendor info */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Vendor / Contractor *</label>
                    <input className="form-input" value={quoteForm.vendor_name} onChange={(e) => setQuoteForm((f) => ({ ...f, vendor_name: e.target.value }))} placeholder="Company name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contact Name</label>
                    <input className="form-input" value={quoteForm.vendor_contact} onChange={(e) => setQuoteForm((f) => ({ ...f, vendor_contact: e.target.value }))} placeholder="Jane Smith" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-input" type="email" value={quoteForm.vendor_email} onChange={(e) => setQuoteForm((f) => ({ ...f, vendor_email: e.target.value }))} placeholder="contact@vendor.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-input" type="tel" value={quoteForm.vendor_phone} onChange={(e) => setQuoteForm((f) => ({ ...f, vendor_phone: e.target.value }))} placeholder="(555) 000-0000" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Total Quote Amount ($)</label>
                  <input className="form-input" type="number" min="0" step="0.01" value={quoteForm.quote_amount} onChange={(e) => setQuoteForm((f) => ({ ...f, quote_amount: e.target.value }))} placeholder="0.00" />
                </div>

                {/* Quote line items */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ ...labelStyle, margin: 0 }}>Line Items</label>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQuoteForm((f) => ({ ...f, line_items: [...f.line_items, { description: '', quantity: 1, unit_price: 0, total: 0 }] }))}>
                      <Plus size={13} /> Add Row
                    </button>
                  </div>
                  {quoteForm.line_items.length > 0 && (
                    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 80px 28px', gap: 6, padding: '5px 8px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)', fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                        <span>Description</span><span>Qty</span><span>Unit Price</span><span />
                      </div>
                      {quoteForm.line_items.map((li, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 80px 28px', gap: 6, padding: '5px 8px', alignItems: 'center', borderBottom: idx < quoteForm.line_items.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                          <input value={li.description} onChange={(e) => {
                            const items = [...quoteForm.line_items];
                            items[idx] = { ...items[idx], description: e.target.value };
                            setQuoteForm((f) => ({ ...f, line_items: items }));
                          }} placeholder="Item" style={{ ...inputStyle, padding: '4px 6px', fontSize: '0.8rem' }} />
                          <input type="number" min="0" value={li.quantity} onChange={(e) => {
                            const items = [...quoteForm.line_items];
                            const qty = parseFloat(e.target.value) || 0;
                            items[idx] = { ...items[idx], quantity: qty, total: qty * items[idx].unit_price };
                            setQuoteForm((f) => ({ ...f, line_items: items }));
                          }} style={{ ...inputStyle, padding: '4px 6px', fontSize: '0.8rem' }} />
                          <input type="number" min="0" step="0.01" value={li.unit_price} onChange={(e) => {
                            const items = [...quoteForm.line_items];
                            const up = parseFloat(e.target.value) || 0;
                            items[idx] = { ...items[idx], unit_price: up, total: items[idx].quantity * up };
                            setQuoteForm((f) => ({ ...f, line_items: items }));
                          }} style={{ ...inputStyle, padding: '4px 6px', fontSize: '0.8rem' }} />
                          <button type="button" onClick={() => setQuoteForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-danger)', padding: 2 }}>
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Notes / Scope</label>
                  <textarea className="form-input" rows={3} value={quoteForm.notes} onChange={(e) => setQuoteForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Terms, validity period, scope…" style={{ resize: 'vertical' }} />
                </div>

                {/* File attach */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '6px 12px' }}>
                    <Upload size={13} />
                    {quoteFile ? quoteFile.name : 'Attach file (PDF, DOC, image)'}
                    <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }} onChange={(e) => setQuoteFile(e.target.files?.[0] ?? null)} />
                  </label>
                  {quoteFile && (
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }} onClick={() => { setQuoteFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                      <X size={13} />
                    </button>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
                <button className="btn btn-ghost" onClick={() => setAddBidOpen(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleAddQuote} disabled={quoteSaving || !quoteForm.vendor_name.trim()}>
                  {quoteSaving ? 'Adding…' : 'Add Bid'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Edit Bid modal */}
        {editBidOpen && editingQuote && (
          <div className="modal-overlay" style={{ zIndex: 500 }}>
            <div className="modal" style={{ maxWidth: 520, width: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0 }}>Edit Bid</h3>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditBidOpen(false)}><X size={16} /></button>
              </div>

              {editQuoteError && (
                <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '0.875rem' }}>
                  <AlertCircle size={14} style={{ display: 'inline', marginRight: 6 }} />{editQuoteError}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Vendor / Contractor *</label>
                    <input className="form-input" value={editQuoteForm.vendor_name} onChange={(e) => setEditQuoteForm((f) => ({ ...f, vendor_name: e.target.value }))} placeholder="Company name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contact Name</label>
                    <input className="form-input" value={editQuoteForm.vendor_contact} onChange={(e) => setEditQuoteForm((f) => ({ ...f, vendor_contact: e.target.value }))} placeholder="Jane Smith" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-input" type="email" value={editQuoteForm.vendor_email} onChange={(e) => setEditQuoteForm((f) => ({ ...f, vendor_email: e.target.value }))} placeholder="contact@vendor.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-input" type="tel" value={editQuoteForm.vendor_phone} onChange={(e) => setEditQuoteForm((f) => ({ ...f, vendor_phone: e.target.value }))} placeholder="(555) 000-0000" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Total Quote Amount ($)</label>
                  <input className="form-input" type="number" min="0" step="0.01" value={editQuoteForm.quote_amount} onChange={(e) => setEditQuoteForm((f) => ({ ...f, quote_amount: e.target.value }))} placeholder="0.00" />
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ ...labelStyle, margin: 0 }}>Line Items</label>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditQuoteForm((f) => ({ ...f, line_items: [...f.line_items, { description: '', quantity: 1, unit_price: 0, total: 0 }] }))}>
                      <Plus size={13} /> Add Row
                    </button>
                  </div>
                  {editQuoteForm.line_items.length > 0 && (
                    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 80px 28px', gap: 6, padding: '5px 8px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)', fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                        <span>Description</span><span>Qty</span><span>Unit Price</span><span />
                      </div>
                      {editQuoteForm.line_items.map((li, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 80px 28px', gap: 6, padding: '5px 8px', alignItems: 'center', borderBottom: idx < editQuoteForm.line_items.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                          <input value={li.description} onChange={(e) => {
                            const items = [...editQuoteForm.line_items];
                            items[idx] = { ...items[idx], description: e.target.value };
                            setEditQuoteForm((f) => ({ ...f, line_items: items }));
                          }} placeholder="Item" style={{ ...inputStyle, padding: '4px 6px', fontSize: '0.8rem' }} />
                          <input type="number" min="0" value={li.quantity} onChange={(e) => {
                            const items = [...editQuoteForm.line_items];
                            const qty = parseFloat(e.target.value) || 0;
                            items[idx] = { ...items[idx], quantity: qty, total: qty * items[idx].unit_price };
                            setEditQuoteForm((f) => ({ ...f, line_items: items }));
                          }} style={{ ...inputStyle, padding: '4px 6px', fontSize: '0.8rem' }} />
                          <input type="number" min="0" step="0.01" value={li.unit_price} onChange={(e) => {
                            const items = [...editQuoteForm.line_items];
                            const up = parseFloat(e.target.value) || 0;
                            items[idx] = { ...items[idx], unit_price: up, total: items[idx].quantity * up };
                            setEditQuoteForm((f) => ({ ...f, line_items: items }));
                          }} style={{ ...inputStyle, padding: '4px 6px', fontSize: '0.8rem' }} />
                          <button type="button" onClick={() => setEditQuoteForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-danger)', padding: 2 }}>
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Notes / Scope</label>
                  <textarea className="form-input" rows={3} value={editQuoteForm.notes} onChange={(e) => setEditQuoteForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Terms, validity period, scope…" style={{ resize: 'vertical' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
                <button className="btn btn-ghost" onClick={() => setEditBidOpen(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleUpdateQuote} disabled={editQuoteSaving || !editQuoteForm.vendor_name.trim()}>
                  {editQuoteSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI PDF extraction modal */}
        {aiModalOpen && ReactDOM.createPortal(
          <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal" style={{ maxWidth: 420, width: '95vw', zIndex: 1001 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Sparkles size={18} style={{ color: 'var(--accent-primary)' }} />
                  <h3 style={{ margin: 0 }}>Import Proposal</h3>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setAiModalOpen(false)}><X size={16} /></button>
              </div>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
                Upload a contractor's proposal PDF, DOC, or DOCX. AI will extract vendor info, line items, and total cost to pre-fill the bid form.
              </p>
              {aiError && (
                <div style={{ padding: '10px 14px', marginBottom: 14, borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '0.875rem' }}>{aiError}</div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', border: '2px dashed var(--border-default)', borderRadius: 8, padding: '20px 16px', justifyContent: 'center', flexDirection: 'column', background: aiPdfFile ? 'rgba(59,130,246,0.05)' : 'transparent' }}>
                <FileText size={28} style={{ color: aiPdfFile ? 'var(--accent-primary)' : 'var(--text-muted)' }} />
                <span style={{ fontSize: '0.875rem', color: aiPdfFile ? 'var(--accent-primary)' : 'var(--text-muted)', fontWeight: aiPdfFile ? 600 : 400 }}>
                  {aiPdfFile ? aiPdfFile.name : 'Click to select PDF, DOC, or DOCX'}
                </span>
                <input ref={aiPdfInputRef} type="file" accept="application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" style={{ display: 'none' }} onChange={(e) => setAiPdfFile(e.target.files?.[0] ?? null)} />
              </label>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button className="btn btn-ghost" onClick={() => setAiModalOpen(false)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={handleAiExtract}
                  disabled={!aiPdfFile || aiExtracting}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  {aiExtracting ? (
                    <><div className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> {AI_LOADING_MSGS[aiLoadingMsgIdx]}</>
                  ) : (
                    <><Sparkles size={14} /> Extract with AI</>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Add Subcontract modal */}
        {addSubOpen && selectedProject && (
          <div className="modal-overlay" style={{ zIndex: 510 }}>
            <div className="modal" style={{ maxWidth: 520, width: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Add Subcontract</h3>
                <button className="btn btn-ghost btn-sm" onClick={() => setAddSubOpen(false)}><X size={16} /></button>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--border-subtle)' }}>
                {([{ id: 'manual', label: 'Enter Manually' }, { id: 'pdf', label: 'Extract from PDF' }, { id: 'existing', label: 'Use Existing Subcontract' }] as const).map((t) => (
                  <button key={t.id} onClick={() => setSubTab(t.id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '7px 12px', fontSize: '0.8125rem',
                    fontWeight: subTab === t.id ? 600 : 400,
                    color: subTab === t.id ? 'var(--accent-primary)' : 'var(--text-muted)',
                    borderBottom: subTab === t.id ? '2px solid var(--accent-primary)' : '2px solid transparent',
                    marginBottom: -1,
                  }}>{t.label}</button>
                ))}
              </div>

              {/* Tab: Extract from PDF */}
              {subTab === 'pdf' && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', border: '2px dashed var(--border-default)', borderRadius: 8, padding: '16px', justifyContent: 'center', flexDirection: 'column', background: subPdfFile ? 'rgba(59,130,246,0.05)' : 'transparent', marginBottom: 10 }}>
                    <FileText size={24} style={{ color: subPdfFile ? 'var(--accent-primary)' : 'var(--text-muted)' }} />
                    <span style={{ fontSize: '0.8125rem', color: subPdfFile ? 'var(--accent-primary)' : 'var(--text-muted)', fontWeight: subPdfFile ? 600 : 400 }}>
                      {subPdfFile ? subPdfFile.name : 'Click to select PDF, DOC, DOCX, or image'}
                    </span>
                    <input ref={subPdfInputRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }} onChange={(e) => setSubPdfFile(e.target.files?.[0] ?? null)} />
                  </label>
                  {subExtractError && <div style={{ color: '#ef4444', fontSize: '0.8rem', marginBottom: 8 }}>{subExtractError}</div>}
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!subPdfFile || subExtracting}
                    onClick={handleSubAiExtract}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {subExtracting ? <><div className="loading-spinner" style={{ width: 13, height: 13, borderWidth: 2 }} /> Extracting…</> : <><Sparkles size={13} /> Extract with AI</>}
                  </button>
                </div>
              )}

              {/* Tab: Use Existing Subcontract */}
              {subTab === 'existing' && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ ...labelStyle }}>Copy from existing subcontract</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(() => {
                      const seenVendors = new Set<string>();
                      const allSubs: Array<{ sub: Subcontract; quoteVendor: string }> = [];
                      for (const q of selectedQuotes) {
                        for (const sub of (subcontracts[q.id] ?? [])) {
                          const key = (sub.vendor_name ?? '').toLowerCase().trim();
                          if (seenVendors.has(key)) continue;
                          seenVendors.add(key);
                          allSubs.push({ sub, quoteVendor: q.vendor_name || '(No vendor)' });
                        }
                      }
                      if (allSubs.length === 0) {
                        return <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>No existing subcontracts available</div>;
                      }
                      return allSubs.map(({ sub, quoteVendor }) => (
                        <button key={sub.id} type="button" className="btn btn-ghost"
                          style={{ textAlign: 'left', justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}
                          onClick={() => setSubForm((f) => ({
                            ...f,
                            vendor_name: sub.vendor_name ?? '',
                            amount: sub.amount ?? '',
                            line_items: Array.isArray(sub.line_items) ? sub.line_items : [],
                            notes: sub.notes ?? '',
                          }))}>
                          <span style={{ fontSize: '0.8125rem' }}>
                            <span style={{ color: 'var(--text-muted)' }}>{quoteVendor} › </span>
                            {sub.vendor_name || '(No vendor)'}
                          </span>
                          <span style={{ color: 'var(--accent-success)', fontWeight: 700, marginLeft: 8, flexShrink: 0 }}>
                            {sub.amount ? fmtCurrency(sub.amount) : '—'}
                          </span>
                        </button>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {/* Shared fields (all tabs) */}
              {subError && <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 6, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '0.8rem' }}>{subError}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Vendor Name *</label>
                  <input className="form-input" value={subForm.vendor_name} onChange={(e) => setSubForm((f) => ({ ...f, vendor_name: e.target.value }))} placeholder="Subcontractor name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount ($)</label>
                  <input className="form-input" type="number" min="0" step="0.01" value={subForm.amount} onChange={(e) => setSubForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label className="form-label">Description / Scope</label>
                  <textarea className="form-input" rows={2} value={subForm.description} onChange={(e) => setSubForm((f) => ({ ...f, description: e.target.value }))} placeholder="Brief scope of work…" style={{ resize: 'vertical' }} />
                </div>
                {/* Line items */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <label style={{ ...labelStyle, margin: 0 }}>Line Items</label>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSubForm((f) => ({ ...f, line_items: [...f.line_items, { description: '', quantity: 1, unit_price: 0, total: 0 }] }))}>
                      <Plus size={12} /> Add Row
                    </button>
                  </div>
                  {subForm.line_items.length > 0 && (
                    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 56px 74px 24px', gap: 4, padding: '4px 6px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)', fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                        <span>Description</span><span>Qty</span><span>Unit Price</span><span />
                      </div>
                      {subForm.line_items.map((li, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 56px 74px 24px', gap: 4, padding: '4px 6px', alignItems: 'center', borderBottom: idx < subForm.line_items.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                          <input value={li.description} onChange={(e) => { const items = [...subForm.line_items]; items[idx] = { ...items[idx], description: e.target.value }; setSubForm((f) => ({ ...f, line_items: items })); }} placeholder="Item" style={{ ...inputStyle, padding: '3px 5px', fontSize: '0.78rem' }} />
                          <input type="number" min="0" value={li.quantity} onChange={(e) => { const items = [...subForm.line_items]; const qty = parseFloat(e.target.value) || 0; items[idx] = { ...items[idx], quantity: qty, total: qty * items[idx].unit_price }; setSubForm((f) => ({ ...f, line_items: items })); }} style={{ ...inputStyle, padding: '3px 5px', fontSize: '0.78rem' }} />
                          <input type="number" min="0" step="0.01" value={li.unit_price} onChange={(e) => { const items = [...subForm.line_items]; const up = parseFloat(e.target.value) || 0; items[idx] = { ...items[idx], unit_price: up, total: items[idx].quantity * up }; setSubForm((f) => ({ ...f, line_items: items })); }} style={{ ...inputStyle, padding: '3px 5px', fontSize: '0.78rem' }} />
                          <button type="button" onClick={() => setSubForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-danger)', padding: 1 }}><X size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-input" rows={2} value={subForm.notes} onChange={(e) => setSubForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Additional notes…" style={{ resize: 'vertical' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button className="btn btn-ghost" onClick={() => setAddSubOpen(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleAddSubcontract} disabled={subSaving || !subForm.vendor_name.trim()}>
                  {subSaving ? 'Saving…' : 'Add Subcontract'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Subcontract modal */}
        {editSubOpen && selectedProject && (
          <div className="modal-overlay" style={{ zIndex: 520 }}
            onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
            onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setEditSubOpen(false); mouseDownTarget.current = null; }}
          >
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Edit Subcontract</h3>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditSubOpen(false)}><X size={16} /></button>
              </div>
              {editSubError && <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 6, background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '0.8rem' }}>{editSubError}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Vendor Name *</label>
                  <input className="form-input" value={editSubForm.vendor_name} onChange={(e) => setEditSubForm((f) => ({ ...f, vendor_name: e.target.value }))} placeholder="Subcontractor name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount ($)</label>
                  <input className="form-input" type="number" min="0" step="0.01" value={editSubForm.amount} onChange={(e) => setEditSubForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label className="form-label">Description / Scope</label>
                  <textarea className="form-input" rows={2} value={editSubForm.description} onChange={(e) => setEditSubForm((f) => ({ ...f, description: e.target.value }))} placeholder="Brief scope of work…" style={{ resize: 'vertical' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-input" rows={2} value={editSubForm.notes} onChange={(e) => setEditSubForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Additional notes…" style={{ resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button className="btn btn-ghost" onClick={() => setEditSubOpen(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleUpdateSubcontract} disabled={editSubSaving || !editSubForm.vendor_name.trim()}>
                  {editSubSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Advance Stage modal */}
        {advanceModalOpen && selectedProject && (() => {
          const ci = getWorkflowIndex(selectedProject.workflow_status);
          const targetIdx = advanceDirection === 'forward' ? ci + 1 : ci - 1;
          const targetStage = WORKFLOW_STAGES[targetIdx];
          const isApprovalStep = targetStage?.key === 'approved';
          const canApprove = user?.role === 'org_admin' || user?.role === 'admin';
          const isSchedulingStep = selectedProject.workflow_status === 'approved' && advanceDirection === 'forward' && targetStage?.key === 'scheduling';
          const isCompletedStep = advanceDirection === 'forward' && targetStage?.key === 'completed';
          const recommendedQuote = selectedQuotes.find((q) => q.is_recommended);
          const noBidSelected = isSchedulingStep && !recommendedQuote;
          const isDisabled = advanceSaving || (isApprovalStep && !canApprove) || noBidSelected;
          return (
            <div className="modal-overlay" style={{ zIndex: 520 }}>
              <div className="modal" style={{ maxWidth: 420, width: '95vw' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h3 style={{ margin: 0 }}>{advanceDirection === 'forward' ? 'Advance Stage' : 'Go Back'}</h3>
                  <button className="btn btn-ghost btn-sm" onClick={() => setAdvanceModalOpen(false)}><X size={16} /></button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ padding: '6px 14px', borderRadius: 999, fontSize: '0.8125rem', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }}>
                      {getWorkflowLabel(selectedProject.workflow_status)}
                    </div>
                    {advanceDirection === 'forward' ? <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} /> : <ChevronLeft size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                    <div style={{ padding: '6px 14px', borderRadius: 999, fontSize: '0.8125rem', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.4)', color: 'var(--accent-primary)', fontWeight: 600 }}>
                      {targetStage?.label}
                    </div>
                  </div>
                  {isApprovalStep && (
                    <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: 'var(--accent-warning)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <AlertCircle size={15} style={{ flexShrink: 0 }} />
                      Only Org Admin can approve this stage.
                    </div>
                  )}
                  {noBidSelected && (
                    <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <AlertCircle size={15} style={{ flexShrink: 0 }} />
                      Please select a bid first using the &lsquo;Select&rsquo; button on a bid card before advancing.
                    </div>
                  )}
                  {isSchedulingStep && recommendedQuote && (
                    <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)', color: 'var(--accent-success)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Check size={15} style={{ flexShrink: 0 }} />
                      Selected bid: <strong style={{ marginLeft: 4 }}>{recommendedQuote.vendor_name ?? '—'}</strong>
                      {recommendedQuote.quote_amount && (
                        <span style={{ marginLeft: 4, color: 'var(--text-secondary)' }}>— ${Number(recommendedQuote.quote_amount).toLocaleString()}</span>
                      )}
                    </div>
                  )}
                  {isCompletedStep && (
                    <div className="form-group">
                      <label className="form-label">Completed on</label>
                      <DatePicker
                        value={completedDateInput}
                        onChange={(v) => setCompletedDateInput(v)}
                        placeholder="Select a date"
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Notes (optional)</label>
                    <textarea className="form-input" rows={3} value={advanceNotes} onChange={(e) => setAdvanceNotes(e.target.value)} placeholder="Reason for stage change…" style={{ resize: 'vertical' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                  <button className="btn btn-ghost" onClick={() => setAdvanceModalOpen(false)}>Cancel</button>
                  <button
                    className="btn btn-primary"
                    onClick={handleAdvanceStage}
                    disabled={isDisabled}
                    style={{ opacity: isDisabled ? 0.5 : 1 }}
                  >
                    {advanceSaving ? 'Saving…' : advanceDirection === 'forward' ? 'Advance' : 'Go Back'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Approval Email modal */}
        {approvalEmailOpen && selectedProject && ReactDOM.createPortal(
          <>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)' }}
              onClick={(e) => { if (e.target === e.currentTarget) setApprovalEmailOpen(false); }}
            />
            <div style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
              zIndex: 1001,
              background: 'rgba(20,30,50,0.97)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 14,
              width: 640, maxWidth: '90vw', maxHeight: '85vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              {/* Fixed header */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(20,30,50,0.97)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Send size={16} style={{ color: 'var(--accent-primary)' }} />
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Send for Approval</h3>
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 3 }}>{selectedProject.name}</div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setApprovalEmailOpen(false)}><X size={16} /></button>
              </div>
              {/* Scrollable body */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              {(() => {
                const toSuggestions = approvalToFocused && approvalTo.length > 0
                  ? savedEmailsRef.current.filter(h => h.toLowerCase().includes(approvalTo.toLowerCase()) && h !== approvalTo)
                  : [];
                const _ccParts = approvalCc.split(',');
                const currentCCSeg = (_ccParts[_ccParts.length - 1] ?? '').trim();
                const ccSuggestions = approvalCcFocused && currentCCSeg.length > 0
                  ? savedEmailsRef.current.filter(h => h.toLowerCase().includes(currentCCSeg.toLowerCase()) && !approvalCc.split(',').map((s: string) => s.trim()).filter(Boolean).includes(h))
                  : [];
                return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group" style={{ position: 'relative' }}>
                  <label className="form-label">To *</label>
                  <input
                    className="form-input"
                    type="email"
                    value={approvalTo}
                    onChange={(e) => setApprovalTo(e.target.value)}
                    onFocus={() => setApprovalToFocused(true)}
                    onBlur={() => setTimeout(() => setApprovalToFocused(false), 150)}
                    placeholder="approver@example.com"
                  />
                  {toSuggestions.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxHeight: 160, overflowY: 'auto' }}>
                      {toSuggestions.map((s: string) => (
                        <div key={s} onMouseDown={() => { setApprovalTo(s); setApprovalToFocused(false); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)' }}
                          onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'}
                          onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group" style={{ position: 'relative' }}>
                  <label className="form-label">CC <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(comma-separated)</span></label>
                  <input
                    className="form-input"
                    type="text"
                    value={approvalCc}
                    onChange={(e) => setApprovalCc(e.target.value)}
                    onFocus={() => setApprovalCcFocused(true)}
                    onBlur={() => setTimeout(() => setApprovalCcFocused(false), 150)}
                    placeholder="cc@example.com, cc2@example.com"
                  />
                  {ccSuggestions.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxHeight: 160, overflowY: 'auto' }}>
                      {ccSuggestions.map((s: string) => (
                        <div key={s} onMouseDown={() => { const parts = approvalCc.split(','); parts[parts.length - 1] = ' ' + s; setApprovalCc(parts.join(',') + ', '); setApprovalCcFocused(false); }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)' }}
                          onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'}
                          onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-input" rows={3} value={approvalNotes} onChange={(e) => setApprovalNotes(e.target.value)} placeholder="Any additional context…" style={{ resize: 'vertical' }} />
                </div>
                {/* Proposal Documents — all quotes, upload button for those without file */}
                {selectedQuotes.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Proposal Documents</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--bg-surface)' }}>
                      {selectedQuotes.map((q) => {
                        if (q.file_url) {
                          const fileName = q.file_url.split('/').pop() ?? 'document';
                          const checked = approvalPdfIds.has(q.id);
                          return (
                            <label key={q.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.8125rem' }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setApprovalPdfIds((prev) => {
                                    const next = new Set(prev);
                                    next.has(q.id) ? next.delete(q.id) : next.add(q.id);
                                    return next;
                                  });
                                }}
                              />
                              <FileText size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                              <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{q.vendor_name ?? '—'}</span>
                              <a href={q.file_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem', textDecoration: 'underline' }}>{fileName}</a>
                            </label>
                          );
                        } else {
                          const isUploading = quoteFileUploading.has(q.id);
                          return (
                            <div key={q.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem' }}>
                              <FileText size={13} style={{ color: 'var(--text-muted)', flexShrink: 0, opacity: 0.4 }} />
                              <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{q.vendor_name ?? '—'}</span>
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>No file</span>
                              <label style={{ marginLeft: 'auto', cursor: isUploading ? 'default' : 'pointer' }}>
                                <input
                                  type="file"
                                  accept="application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                  style={{ display: 'none' }}
                                  disabled={isUploading}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleQuoteFileUpload(q.id, file);
                                    e.target.value = '';
                                  }}
                                />
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '3px 8px', borderRadius: 'var(--radius-sm)',
                                  border: '1px solid var(--border-default)',
                                  background: 'var(--bg-elevated)',
                                  color: 'var(--text-secondary)',
                                  fontSize: '0.75rem', fontWeight: 500,
                                  opacity: isUploading ? 0.6 : 1,
                                }}>
                                  <Upload size={11} />
                                  {isUploading ? 'Uploading…' : 'Upload file'}
                                </span>
                              </label>
                            </div>
                          );
                        }
                      })}
                      <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        Checked PDFs will be included as clickable links in the email body.
                      </p>
                    </div>
                  </div>
                )}
                {/* Preview */}
                <div style={{ border: '1px solid var(--border-default)', borderRadius: 8, padding: '12px 14px', background: 'var(--bg-surface)', fontSize: '0.8125rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Preview</div>
                    <span style={{
                      fontSize: '0.7rem', borderRadius: 10, padding: '2px 8px', fontWeight: 600,
                      ...(isEnterprise
                        ? { background: 'rgba(139,92,246,0.12)', color: 'var(--accent-purple)' }
                        : isPro
                          ? { background: 'rgba(59,130,246,0.12)', color: 'var(--accent-primary)' }
                          : { background: 'rgba(100,116,139,0.12)', color: '#64748b' })
                    }}>
                      {isEnterprise ? 'Full email with AI analysis' : isPro ? 'Enhanced email with line items' : 'Basic approval email'}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', marginBottom: 6 }}><strong>{selectedProject.name}</strong></div>
                  {selectedQuotes.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', marginBottom: 8 }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-elevated)' }}>
                          <th style={{ padding: '4px 8px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>Vendor</th>
                          <th style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--text-muted)', fontWeight: 600 }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedQuotes.map((q) => (
                          <tr key={q.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                            <td style={{ padding: '4px 8px', color: 'var(--text-primary)' }}>{q.vendor_name ?? '—'}</td>
                            <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--text-primary)', fontWeight: 600 }}>{q.quote_amount ? `$${Number(q.quote_amount).toLocaleString()}` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {bidAnalysis && (
                    <div style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 6, padding: '8px 10px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      <strong style={{ color: 'var(--accent-purple)' }}>AI Recommendation:</strong> {bidAnalysis.recommendation.vendor} — {bidAnalysis.recommendation.reasoning}
                    </div>
                  )}
                </div>
                {/* Additional Documents */}
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 2 }}>Additional Documents</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Attach supporting files, photos, or any other relevant documents</div>
                  </div>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setApprovalIsDragging(true); }}
                    onDragLeave={() => setApprovalIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setApprovalIsDragging(false);
                      const files = Array.from(e.dataTransfer.files);
                      if (files.length > 0) setAdditionalFiles(prev => [...prev, ...files]);
                    }}
                    onClick={() => additionalFilesInputRef.current?.click()}
                    style={{
                      border: `2px dashed ${approvalIsDragging ? 'rgba(59,130,246,0.5)' : 'rgba(255,255,255,0.15)'}`,
                      borderRadius: 10, padding: 20, textAlign: 'center' as const,
                      background: approvalIsDragging ? 'rgba(59,130,246,0.05)' : 'rgba(255,255,255,0.02)',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    <Upload size={20} style={{ color: 'var(--text-muted)', marginBottom: 6 }} />
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Drop files here or click to browse</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 3 }}>PDF, images, Word docs — any format</div>
                  </div>
                  <input
                    ref={additionalFilesInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      if (files.length > 0) setAdditionalFiles(prev => [...prev, ...files]);
                      e.target.value = '';
                    }}
                  />
                  {additionalFiles.length > 0 && (
                    <div style={{ marginTop: 8, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, overflow: 'hidden' }}>
                      {additionalFiles.map((file, idx) => {
                        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
                        const isPdf = ext === 'pdf';
                        const isImage = ['jpg','jpeg','png','gif','webp'].includes(ext);
                        const isDoc = ['doc','docx'].includes(ext);
                        const iconColor = isPdf ? '#ef4444' : isImage ? '#3b82f6' : isDoc ? '#8b5cf6' : '#6b7280';
                        const sizeStr = file.size < 1024 * 1024
                          ? `${(file.size / 1024).toFixed(1)} KB`
                          : `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: idx < additionalFiles.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none', fontSize: '0.8125rem', background: 'var(--bg-surface)' }}>
                            <FileText size={14} style={{ color: iconColor, flexShrink: 0 }} />
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>{file.name}</span>
                            <span style={{ color: 'var(--text-muted)', flexShrink: 0, fontSize: '0.75rem' }}>{sizeStr}</span>
                            <button
                              type="button"
                              onClick={() => setAdditionalFiles(prev => prev.filter((_, i) => i !== idx))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'flex', alignItems: 'center' }}
                            >
                              <X size={13} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
                );
              })()}
              </div>
              {/* Fixed footer */}
              <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(20,30,50,0.97)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
                <button className="btn btn-ghost" onClick={() => setApprovalEmailOpen(false)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={handleSendApprovalEmail}
                  disabled={approvalSending || !approvalTo.trim()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  {approvalSending ? (approvalUploadProgress || 'Sending…') : <><Send size={14} /> Send Approval Email</>}
                </button>
              </div>
            </div>
          </>,
          document.body
        )}

        {/* Project edit slide-in panel */}
        {panelOpen && renderEditPanel()}

        {/* Delete confirm */}
        {deleteConfirm && renderDeleteConfirm()}
      </div>
    );
  }

  // ── List view (default) ────────────────────────────────────────────────────

  function renderEditPanel() {
    return ReactDOM.createPortal(
      <>
        <div onClick={closePanel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 998, backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }} />
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 560,
          background: 'var(--bg-panel)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: 'var(--shadow-lg)',
          zIndex: 999, display: 'flex', flexDirection: 'column', overflowY: 'auto',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border-default)', position: 'sticky', top: 0, background: 'var(--bg-elevated)', zIndex: 1 }}>
            <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700 }}>{editing ? 'Edit Project' : 'New Project'}</h2>
            <button onClick={closePanel} className="modal-close"><X size={20} /></button>
          </div>
          <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column', gap: 18 }}>
            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: '#f87171', fontSize: '0.875rem' }}>
                <AlertCircle size={16} /> {error}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Project Name *</label>
              <input className="form-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Roof Replacement" />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Brief overview of the project…" rows={3} style={{ resize: 'vertical' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Status</label>
                <StyledSelect value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v as ProjectStatus }))} options={ALL_STATUSES.map((s) => ({ value: s, label: STATUS_CONFIG[s].label, color: STATUS_CONFIG[s].color }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Vendor</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Search vendors..."
                    value={vendorSearch}
                    onChange={e => { setVendorSearch(e.target.value); setVendorDropdownOpen(true); }}
                    onFocus={() => setVendorDropdownOpen(true)}
                    className="form-input"
                    style={{ width: '100%' }}
                  />
                  {vendorDropdownOpen && (
                    <>
                      <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setVendorDropdownOpen(false)} />
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0,
                        background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
                        borderRadius: 8, zIndex: 99, maxHeight: 220, overflowY: 'auto',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.4)', marginTop: 4,
                      }}>
                        <div
                          onMouseDown={() => { setForm(p => ({ ...p, vendor_id: '' })); setVendorSearch(''); setVendorDropdownOpen(false); }}
                          style={{ padding: '9px 14px', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, borderBottom: '1px solid var(--border-subtle)' }}
                        >
                          — None —
                        </div>
                        {vendors
                          .filter(v => v.name.toLowerCase().includes(vendorSearch.toLowerCase()))
                          .map(v => (
                            <div
                              key={v.id}
                              onMouseDown={() => { setForm(p => ({ ...p, vendor_id: String(v.id) })); setVendorSearch(v.name); setVendorDropdownOpen(false); }}
                              style={{
                                padding: '9px 14px', cursor: 'pointer', fontSize: 14,
                                background: form.vendor_id === String(v.id) ? 'rgba(59,130,246,0.15)' : 'transparent',
                                color: form.vendor_id === String(v.id) ? 'var(--accent-primary)' : 'var(--text-primary)',
                                borderBottom: '1px solid var(--border-subtle)',
                              }}
                              onMouseEnter={e => { if (form.vendor_id !== String(v.id)) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                              onMouseLeave={e => { if (form.vendor_id !== String(v.id)) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                            >
                              {v.name}
                            </div>
                          ))}
                        {vendors.filter(v => v.name.toLowerCase().includes(vendorSearch.toLowerCase())).length === 0 && (
                          <div style={{ padding: '9px 14px', color: 'var(--text-muted)', fontSize: 14 }}>No vendors found</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Estimated Cost ($)</label>
                <input className="form-input" type="number" min="0" step="0.01" value={form.estimated_cost} onChange={(e) => setForm((f) => ({ ...f, estimated_cost: e.target.value }))} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Actual Cost ($)</label>
                <input className="form-input" type="number" min="0" step="0.01" value={form.actual_cost} onChange={(e) => setForm((f) => ({ ...f, actual_cost: e.target.value }))} placeholder="0.00" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <DatePicker value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} placeholder="Select a date" />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <DatePicker value={form.end_date} onChange={(v) => setForm((f) => ({ ...f, end_date: v }))} placeholder="Select a date" />
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <label style={{ ...labelStyle, margin: 0 }}>Line Items</label>
                <button type="button" onClick={addLineItem} className="btn btn-ghost btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem' }}>
                  <Plus size={13} /> Add Row
                </button>
              </div>
              {form.line_items.length > 0 && (
                <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 28px', gap: 8, padding: '7px 10px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                    <span>Description</span><span>Qty</span><span>Unit Cost</span><span />
                  </div>
                  {form.line_items.map((li, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 28px', gap: 8, padding: '7px 10px', alignItems: 'center', borderBottom: idx < form.line_items.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                      <input value={li.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} placeholder="Item description" style={{ ...inputStyle, padding: '5px 8px', fontSize: '0.8125rem' }} />
                      <input type="number" min="0" step="0.01" value={li.quantity} onChange={(e) => updateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)} style={{ ...inputStyle, padding: '5px 8px', fontSize: '0.8125rem' }} />
                      <input type="number" min="0" step="0.01" value={li.unit_cost} onChange={(e) => updateLineItem(idx, 'unit_cost', parseFloat(e.target.value) || 0)} style={{ ...inputStyle, padding: '5px 8px', fontSize: '0.8125rem' }} />
                      <button type="button" onClick={() => removeLineItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-danger)', display: 'flex', alignItems: 'center', padding: 2 }}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '7px 12px', borderTop: '1px solid var(--border-default)', background: 'var(--bg-surface)', fontSize: '0.8125rem', fontWeight: 700 }}>
                    Subtotal: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(lineItemTotal(form.line_items))}
                  </div>
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Additional notes or context…" rows={3} style={{ resize: 'vertical' }} />
            </div>
          </div>
          <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border-default)', display: 'flex', justifyContent: 'flex-end', gap: 10, position: 'sticky', bottom: 0, background: 'var(--bg-elevated)' }}>
            <button onClick={closePanel} className="btn btn-ghost">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="btn btn-primary" style={{ opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Project'}
            </button>
          </div>
        </div>
      </>,
      document.body
    );
  }

  function renderDeleteConfirm() {
    return (
      <>
        <div onClick={() => setDeleteConfirm(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500 }} />
        <div className="card" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 501, padding: 28, width: 380, maxWidth: '90vw' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <AlertCircle size={22} color="var(--accent-danger)" />
            <h3 style={{ margin: 0, fontSize: '1rem' }}>Delete Project?</h3>
          </div>
          <p style={{ margin: '0 0 20px', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            <strong>{deleteConfirm!.name}</strong> and all its line items and quotes will be permanently deleted.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={() => setDeleteConfirm(null)} className="btn btn-ghost">Cancel</button>
            <button onClick={() => handleDelete(deleteConfirm!)} className="btn btn-danger">Delete</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <div>
      {/* Focus mode toggle — same floating-action-cluster (AppLayout.css) + PageFocusToggle
          Dashboard.tsx uses. */}
      <div className="floating-action-cluster"><PageFocusToggle /></div>
      {/* Demo-mode toast */}
      {demoToast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#b45309', color: '#fff', borderRadius: 8, padding: '10px 20px', fontSize: '0.875rem', fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
          ⚠ {demoToast}
        </div>
      )}
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <FolderKanban size={22} color="var(--accent-primary)" />
          <h1 className="page-title">Projects</h1>
          <span style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)', borderRadius: 'var(--radius-full, 999px)', padding: '2px 10px', fontSize: '0.8125rem', fontWeight: 600 }}>
            {filtered.length}
          </span>
          {/* AI usage indicator */}
          {isEnterprise ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: 'rgba(34,197,94,0.12)', color: '#16a34a' }}>
              <Sparkles size={12} /> AI: Unlimited
            </span>
          ) : isPro ? (
            (() => {
              const remaining = aiLimit - aiUsed;
              if (remaining > 0) {
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: 'rgba(59,130,246,0.12)', color: 'var(--accent-primary)' }}>
                    AI: {remaining}/{aiLimit} remaining
                  </span>
                );
              }
              const m = aiUsageData?.data?.month;
              const resetDate = (() => {
                if (!m) return 'next month';
                const [y, mo] = m.split('-').map(Number);
                return new Date(y, mo, 1).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              })();
              return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: 'rgba(245,158,11,0.12)', color: '#b45309' }}>
                  AI: Limit reached · Resets {resetDate}
                </span>
              );
            })()
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: 'rgba(100,116,139,0.1)', color: 'var(--text-muted)' }}>
              <Lock size={12} /> No AI · Upgrade to Pro
            </span>
          )}
        </div>
        <button className="btn btn-primary" onClick={() => demoBlock(openCreate)}>
          <Plus size={16} /> New Project
        </button>
      </div>

      {/* Active / Completed tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border-subtle)', paddingBottom: 0 }}>
        {([{ id: 'active', label: 'Active' }, { id: 'completed', label: 'Completed' }] as const).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setWorkflowTab(tab.id)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 16px', fontSize: '0.875rem',
              fontWeight: workflowTab === tab.id ? 600 : 400,
              color: workflowTab === tab.id ? 'var(--accent-primary)' : 'var(--text-muted)',
              borderBottom: workflowTab === tab.id ? '2px solid var(--accent-primary)' : '2px solid transparent',
              marginBottom: -1, transition: 'color 0.15s',
            }}
          >{tab.label}</button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: '1 1 220px', minWidth: 180 }}>
          <Search size={14} color="var(--text-muted)" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search projects…" />
          {search && (
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }} onClick={() => setSearch('')}>
              <X size={13} />
            </button>
          )}
        </div>
        <StyledSelect
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as 'all' | ProjectStatus)}
          options={[{ value: 'all', label: 'All Statuses' }, ...ALL_STATUSES.map((s) => ({ value: s, label: STATUS_CONFIG[s].label, color: STATUS_CONFIG[s].color }))]}
          minWidth={160}
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="loading-spinner" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <FolderKanban size={44} />
          <h3>No projects found</h3>
          <p>{search ? 'Try adjusting your search.' : 'Create your first project to get started.'}</p>
          {!search && <button className="btn btn-primary" onClick={() => demoBlock(openCreate)}><Plus size={16} /> New Project</button>}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                {['Project', 'Status', 'Vendor', 'Est. Cost', 'Actual Cost', 'Date Range', 'Actions'].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const sc = STATUS_CONFIG[p.status];
                const liTotal = p.line_items.reduce((s, li) => s + parseFloat(String(li.total_cost ?? 0)), 0);
                return (
                  <tr
                    key={p.id}
                    style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'background 0.1s' }}
                    onClick={() => openProject(p)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '12px 12px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem' }}>{p.name}</div>
                      {p.description && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: 2, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description}</div>
                      )}
                      {p.line_items.length > 0 && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginTop: 3 }}>
                          {p.line_items.length} line item{p.line_items.length !== 1 ? 's' : ''} · {fmtCurrency(liTotal)}
                        </div>
                      )}
                      {/* Workflow progress bar */}
                      {(() => {
                        const wIdx = getWorkflowIndex(p.workflow_status ?? 'obtaining_bids');
                        const pct = (wIdx / (WORKFLOW_STAGES.length - 1)) * 100;
                        const barColor = (p.workflow_status === 'completed' || p.workflow_status === 'paid') ? 'var(--accent-success)' : 'var(--accent-primary)';
                        return (
                          <div style={{ marginTop: 6 }}>
                            <div style={{ height: 5, background: 'var(--border-subtle)', borderRadius: 999, overflow: 'hidden', maxWidth: 200 }}>
                              <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 999, transition: 'width 0.3s' }} />
                            </div>
                            <div style={{ marginTop: 4 }}>
                              <span style={{
                                display: 'inline-block', padding: '1px 7px', borderRadius: 999,
                                fontSize: '0.6875rem', fontWeight: 600,
                                background: (p.workflow_status === 'completed' || p.workflow_status === 'paid') ? 'rgba(16,185,129,0.12)' : 'rgba(59,130,246,0.12)',
                                color: (p.workflow_status === 'completed' || p.workflow_status === 'paid') ? 'var(--accent-success)' : 'var(--accent-primary)',
                              }}>
                                {getWorkflowLabel(p.workflow_status ?? 'obtaining_bids')}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '12px 12px' }}>
                      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700, color: sc.color, background: sc.bg }}>{sc.label}</span>
                    </td>
                    <td style={{ padding: '12px 12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{p.vendor_name ?? '—'}</td>
                    <td style={{ padding: '12px 12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><DollarSign size={12} style={{ opacity: 0.4 }} />{fmtCurrency(p.estimated_cost)}</div>
                    </td>
                    <td style={{ padding: '12px 12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><DollarSign size={12} style={{ opacity: 0.4 }} />{fmtCurrency(p.actual_cost)}</div>
                    </td>
                    <td style={{ padding: '12px 12px', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={12} style={{ opacity: 0.4 }} />
                        {p.start_date ? fmtProjectDate(p.start_date) : '—'}
                        {p.start_date && p.end_date && <span style={{ opacity: 0.5 }}>→</span>}
                        {p.end_date && fmtProjectDate(p.end_date)}
                      </div>
                    </td>
                    <td style={{ padding: '12px 12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={(e) => { e.stopPropagation(); openEdit(p); }} className="btn btn-ghost" style={{ padding: '4px 8px' }} title="Edit">
                          <Pencil size={13} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(p); }} className="btn btn-ghost" style={{ padding: '4px 8px', color: 'var(--accent-danger)' }} title="Delete">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Slide-in Panel */}
      {panelOpen && renderEditPanel()}

      {/* Delete Confirmation */}
      {deleteConfirm && renderDeleteConfirm()}
    </div>
  );
}
